import React, { useState, useEffect, memo, useCallback, forwardRef, useRef, useImperativeHandle, ForwardedRef, Suspense } from 'react';
import { View, Image, useWindowDimensions, Pressable, Platform, StyleSheet, ScrollView, TextInput } from 'react-native';
import styles from '@stylesheet';
import { FastField, FieldHelperProps, FieldInputProps, FieldMetaProps, FieldProps, useFormikContext } from 'formik';
import moment from 'moment';
// import { DatePickerModal, TimePickerModal } from 'tamagui-dates';
import { Switch } from 'tamagui';
import DropDownPicker from 'react-native-dropdown-picker';
import toTitleCase from './toTitleCase';
import TagInput from '../components/Global/TagInput';
import { Modal, Text } from '@constants/Themed';
import InputFieldStyled from '@components/Global/InputFieldStyled';
import ImagePickerComponent, { ImagePickerHandle } from '../components/formComponents/ImageSelection';
import Button from '../components/UI/Button/Button';
// import DatePicker from 'react-native-date-picker';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { Entypo, FontAwesome } from '@expo/vector-icons';
import type { GooglePlacesAutocompleteRef } from 'react-native-google-places-autocomplete';
import { USER_API_BASE_URL } from '@api/constant';
import type {
  GenerateGetCityProps,
  HandleFieldTypeProps,
  SchemaItem,
  SchemaItemContent,
  SchemaValidation,
  DropdownItem,
  ProductItem,
  TemplateItem,
  CRMItem,
  PriceValue,
  DateRange,
  TimeValue,
  ImageSelection,
  CountryItem,
  CurrencyItem,
  TimezoneOption,
  FieldType,
  ThemeType,
} from '@types/handleFieldType';

// Dynamic import to avoid TDZ bug during module evaluation
let GooglePlacesAutocomplete: any = null;
let isGooglePlacesLoaded = false;
let isLoading = false;

const loadGooglePlaces = async () => {
  if (isGooglePlacesLoaded && GooglePlacesAutocomplete) {
    return GooglePlacesAutocomplete;
  }

  // Prevent concurrent loads
  if (isLoading) {
    // Wait for ongoing load to complete
    let attempts = 0;
    while (isLoading && attempts < 50) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
      if (isGooglePlacesLoaded && GooglePlacesAutocomplete) {
        return GooglePlacesAutocomplete;
      }
    }
  }

  try {
    isLoading = true;

    // Import the module
    const module = await import('react-native-google-places-autocomplete');

    // Wait a bit to ensure module is fully evaluated
    await new Promise(resolve => setTimeout(resolve, 50));

    // Extract the component
    const Component = module?.GooglePlacesAutocomplete;

    if (!Component) {
      throw new Error('GooglePlacesAutocomplete component not found in module');
    }

    // Verify it's a valid component before caching
    if (typeof Component !== 'function' && typeof Component !== 'object') {
      throw new Error('Invalid component type');
    }

    // Additional delay to ensure all internal properties are initialized
    await new Promise(resolve => setTimeout(resolve, 100));

    GooglePlacesAutocomplete = Component;
    isGooglePlacesLoaded = true;
    isLoading = false;

    return GooglePlacesAutocomplete;
  } catch (error) {
    isLoading = false;
    console.error('Failed to load GooglePlacesAutocomplete:', error);
    throw error;
  }
};
import { AntDesign } from '@expo/vector-icons';
import { useField } from 'formik';

// Removed unused utility function - blur prevention is now handled inline in each input
import Checkbox from 'expo-checkbox';
import { debounce, pick } from 'lodash';
import UserFinder from '@components/Global/UserFinder/UserFinder';
import CurrencyExtractor from '@utils/stringUtils/currencyList';
// Lazy import to avoid circular dependency and initialization issues
let apisCache: any = null;
const getApis = async () => {
  if (!apisCache) {
    apisCache = await import('@api/api').then(m => m.default);
  }
  return apisCache;
};
import CRMList from '@components/Global/CRMFinder/CRMList';
import TemplateListItem from '@components/Global/ListTypes/TemplateListItem';
import { MenuItem, TextField } from '@mui/material';
import { DateField, DatePicker, DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import genCountryList from './genCountryList';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { DateRangePicker } from 'react-date-range';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css'
import { Slider, Select } from '@mui/material';
import HorizontalCarousel from '@components/Global/Carousel/HorizontalCarousel';
import { Label, Separator, SizableText, Input, TextArea, XStack, YStack } from 'tamagui';
import ErrorBoundary from '@utils/ErrorBoundary';
import { GetField } from './rowComp';
import { Slider as MatSlider, Autocomplete } from '@mui/material';
import MapView from '@navigation/screens/MidLevel/NetworkingScreen/components/MapView';
import ColorPicker from './subcomponents/ColorPicker';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import DataItemSelection from '@components/Global/DataItemSelection';
import FormControl from '@mui/material/FormControl';
import { blurPreventionState } from '@utils/blurPrevention';

// Utility function to normalize border styles and prevent conflicts
const normalizeBorderStyles = (styles: any) => {
  if (!styles) return styles;
  
  const normalized = { ...styles };
  
  // Handle borderRadius conflicts
  if (normalized.borderRadius && (
    normalized.borderTopLeftRadius ||
    normalized.borderTopRightRadius ||
    normalized.borderBottomLeftRadius ||
    normalized.borderBottomRightRadius
  )) {
    delete normalized.borderRadius;
  }

  // If both borderColor and specific border colors are present, use specific ones
  if (normalized.borderColor && (normalized.borderTopColor || normalized.borderRightColor || normalized.borderBottomColor || normalized.borderLeftColor)) {
    // Remove the shorthand borderColor to avoid conflicts
    delete normalized.borderColor;
  }
  
  // Handle borderWidth conflicts
  if (normalized.borderWidth && (
    normalized.borderTopWidth ||
    normalized.borderRightWidth ||
    normalized.borderBottomWidth ||
    normalized.borderLeftWidth
  )) {
    delete normalized.borderWidth;
  }

  // Handle borderStyle conflicts
  if (normalized.borderStyle && (
    normalized.borderTopStyle ||
    normalized.borderRightStyle ||
    normalized.borderBottomStyle ||
    normalized.borderLeftStyle
  )) {
    delete normalized.borderStyle;
  }

  return normalized;
};
import InputLabel from '@mui/material/InputLabel';

const style = document.createElement('style');
style.innerHTML = `
.rdrDateRangePickerWrapper { padding: 30px !important}
/* Hide the specific special-label class */
.special-label {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  height: 0 !important;
  width: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  position: absolute !important;
  left: -9999px !important;
}
/* Also hide any other potential labels */
.react-tel-input label,
.react-tel-input .phone-label,
.react-tel-input .input-label,
.react-tel-input .floating-label,
.react-tel-input .material-label {
  display: none !important;
  visibility: hidden !important;
}
/* Fix phone input overlap - ensure input is positioned correctly */
.phone-input-wrapper .react-tel-input,
.react-tel-input {
  position: relative !important;
  width: 100% !important;
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
}
.phone-input-wrapper .react-tel-input .flag-dropdown,
.react-tel-input .flag-dropdown {
  position: absolute !important;
  left: 0 !important;
  top: 0 !important;
  bottom: 0 !important;
  width: 70px !important;
  max-width: 80px !important;
  z-index: 10 !important;
  background: transparent !important;
  border: none !important;
  pointer-events: auto !important;
}
.phone-input-wrapper .react-tel-input .selected-flag,
.react-tel-input .selected-flag {
  width: 70px !important;
  max-width: 80px !important;
  padding: 0 8px !important;
  background: transparent !important;
  cursor: pointer !important;
  pointer-events: auto !important;
  z-index: 11 !important;
  position: relative !important;
}
.phone-input-wrapper .react-tel-input input[type="tel"],
.react-tel-input input[type="tel"] {
  padding-left: 60px !important;
  margin-left: 0 !important;
  width: 100% !important;
  position: relative !important;
  z-index: 1 !important;
  background: transparent !important;
  border: none !important;
  outline: none !important;
  pointer-events: auto !important;
}
/* Ensure input doesn't block button clicks - the padding-left ensures no overlap */
.phone-input-wrapper .react-tel-input .country-list,
.react-tel-input .country-list {
  position: absolute !important;
  z-index: 1002 !important;
  left: 0 !important;
  top: 55px !important;
  width: 400px !important;
  min-width: 400px !important;
  max-width: 400px !important;
  margin: 0 !important;
  user-select: none !important;
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
}
.phone-input-wrapper .react-tel-input .country-list .country,
.react-tel-input .country-list .country {
  padding: 8px 16px !important;
  padding-left: 16px !important;
  user-select: none !important;
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
  display: flex !important;
  align-items: center !important;
  gap: 12px !important;
  cursor: pointer !important;
}
.phone-input-wrapper .react-tel-input .country-list .country *,
.react-tel-input .country-list .country * {
  user-select: none !important;
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
}
.phone-input-wrapper .react-tel-input .country-list .country .flag,
.react-tel-input .country-list .country .flag,
.phone-input-wrapper .react-tel-input .country-list .country [class*="flag"],
.react-tel-input .country-list .country [class*="flag"] {
  flex-shrink: 0 !important;
  margin-right: 12px !important;
  margin-left: 0 !important;
  width: 20px !important;
  height: 15px !important;
  display: inline-block !important;
  vertical-align: middle !important;
}
.phone-input-wrapper .react-tel-input .country-list .country .country-name,
.react-tel-input .country-list .country .country-name,
.phone-input-wrapper .react-tel-input .country-list .country [class*="name"],
.react-tel-input .country-list .country [class*="name"] {
  margin-left: 0 !important;
  padding-left: 0 !important;
  flex: 1 !important;
}
.phone-input-wrapper .react-tel-input .country-list .country .dial-code,
.react-tel-input .country-list .country .dial-code,
.phone-input-wrapper .react-tel-input .country-list .country [class*="dial"],
.react-tel-input .country-list .country [class*="dial"] {
  margin-left: auto !important;
  padding-left: 12px !important;
}`;
document.head.appendChild(style);

const countryList = genCountryList();
const MIN_DROPDOWN_HIEGHT = 200;

// Helper function to replace parseByDelimiter functionality
const formatTitle = (title: string | undefined): string => {
  if (!title) return '';
  return toTitleCase(title.replace(/_/g, ' '));
};

// GenerateGetCityProps is now imported from types

// Wrapper component to prevent TDZ errors by delaying ref attachment
const SafeGooglePlacesWrapper = forwardRef<any, any>((props, ref) => {
  const internalRef = useRef<any>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    // Delay mounting to ensure component is fully initialized
    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Attach ref after component is mounted to prevent TDZ errors
    if (isMounted && internalRef.current && ref) {
      if (typeof ref === 'function') {
        ref(internalRef.current);
      } else if (ref && 'current' in ref) {
        (ref as any).current = internalRef.current;
      }
    }
  }, [isMounted, ref]);

  if (!isMounted) {
    return null;
  }

  const { Component, ...restProps } = props;
  return <Component {...restProps} ref={internalRef} />;
});

// Custom error boundary for Google Places that triggers fallback
class GooglePlacesErrorBoundary extends React.Component<
  { children: React.ReactNode; onError: () => void },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; onError: () => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('GooglePlacesAutocomplete error caught:', error, errorInfo);
    // Trigger fallback callback
    this.props.onError();
  }

  render() {
    if (this.state.hasError) {
      // Return null to let parent render fallback
      return null;
    }
    return this.props.children;
  }
}

const GenerateGetCity = forwardRef<GooglePlacesAutocompleteRef, GenerateGetCityProps>(
  ({ index, focusCallback, blurCallback, length, nextFieldFunction, helpers, field, meta, theme, hasError, schemaItem, updateFunction, initialValue }: GenerateGetCityProps,
    ref
  ) => {
    const [width, setWidth] = useState(0);
    const [storedLocation, setStoredLocation] = useState('');
    const mapRef = useRef(null);

    // Touch device detection and blur prevention for web
    const focusTimeRef = useRef<number>(0);
    const isTouchDeviceRef = useRef<boolean>(false);
    const getCurrentTime = useCallback(() => {
      if (Platform.OS === 'web' && typeof performance !== 'undefined' && performance.now) {
        return performance.now();
      }
      return Date.now();
    }, []);

    useEffect(() => {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        isTouchDeviceRef.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      }
    }, []);

    // Define theme-based colors
    const textColor = theme ? 'black' : 'white';
    const placeholderColor = theme ? 'grey' : '#B0B0B0';
    const bgColor = theme ? 'white' : 'grey';

    // Build requestUrl safely
    const requestUrl = React.useMemo(() => {
      if (Platform.OS !== 'web') return undefined;
      try {
        if (!USER_API_BASE_URL || typeof USER_API_BASE_URL !== 'string' || !USER_API_BASE_URL.includes('http')) {
          return undefined;
        }
        return {
          useOnPlatform: 'web' as const,
          url: `${USER_API_BASE_URL}/proxy/google-map-proxy`,
        };
      } catch (error) {
        console.warn('Error building requestUrl:', error);
        return undefined;
      }
    }, []);

    // Load GooglePlacesAutocomplete dynamically to avoid TDZ bug
    const [GooglePlacesComponent, setGooglePlacesComponent] = useState<any>(null);
    const [isReady, setIsReady] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [runtimeError, setRuntimeError] = useState<boolean>(false);
    const [initTimeout, setInitTimeout] = useState<NodeJS.Timeout | null>(null);

    // Monitor for initialization errors with timeout
    useEffect(() => {
      if (isReady && GooglePlacesComponent && !runtimeError && !loadError) {
        // Set a timeout to fallback if component doesn't render within 2 seconds
        const timeout = setTimeout(() => {
          // Check if component failed to initialize
          console.warn('GooglePlacesAutocomplete initialization timeout, falling back to text input');
          setRuntimeError(true);
        }, 2000);
        setInitTimeout(timeout);

        return () => {
          clearTimeout(timeout);
        };
      } else if (initTimeout) {
        // Clear timeout if error occurred or component not ready
        clearTimeout(initTimeout);
        setInitTimeout(null);
      }
    }, [isReady, GooglePlacesComponent, runtimeError, loadError]);

    useEffect(() => {
      let isMounted = true;

      const loadComponent = async () => {
        try {
          // Wait a bit to ensure React is fully initialized
          await new Promise(resolve => setTimeout(resolve, 200));

          if (!isMounted) return;

          // Load the component dynamically
          const Component = await loadGooglePlaces();

          if (!isMounted) return;

          if (Component) {
            // Verify the component is actually a valid React component
            if (typeof Component !== 'function' && typeof Component !== 'object') {
              throw new Error('Invalid component type');
            }

          // Set the component
            setGooglePlacesComponent(() => Component);

            // Additional delay to ensure module is fully evaluated and all internal properties are initialized
            await new Promise(resolve => setTimeout(resolve, 300));

            if (isMounted) {
              setIsReady(true);
            }
          } else {
            throw new Error('Component is null or undefined');
          }
        } catch (error) {
          console.error('Error loading GooglePlacesAutocomplete:', error);
          if (isMounted) {
            setLoadError(error instanceof Error ? error.message : 'Failed to load Google Places');
            setIsReady(true); // Show fallback
          }
        }
      };

      loadComponent();

      return () => {
        isMounted = false;
      };
    }, []);

    // Use useEffect to catch global errors from this component
    // MUST be called before any early returns to maintain hook order
    useEffect(() => {
      const handleError = (event: ErrorEvent) => {
        if (event.error && event.error.message && event.error.message.includes('_disableRowLoaders')) {
          console.error('GooglePlacesAutocomplete TDZ error detected, falling back to text input');
          setRuntimeError(true);
        }
      };

      const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
        if (event.reason && event.reason.message && event.reason.message.includes('_disableRowLoaders')) {
          console.error('GooglePlacesAutocomplete promise rejection detected, falling back to text input');
          setRuntimeError(true);
        }
      };

      if (Platform.OS === 'web') {
        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);
      }

      return () => {
        if (Platform.OS === 'web') {
          window.removeEventListener('error', handleError);
          window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        }
      };
    }, []);

    // Fallback TextInput component
    const fallbackInputRef = useRef<TextInput>(null);
    const renderFallbackInput = () => (
      <View style={{ flex: 1, marginVertical: 5, left: -5 }}>
        {(loadError || runtimeError) && (
          <Text style={{ color: 'orange', fontSize: 12, marginBottom: 5 }}>
            {loadError || 'Google Places unavailable, using text input'}
          </Text>
        )}
        {hasError && (
          <Text style={{ color: 'red', fontSize: 12, marginBottom: 5 }}>
            {meta.error}
          </Text>
        )}
        <TextInput
          ref={fallbackInputRef}
          placeholder={schemaItem.content?.label || (schemaItem.title ? formatTitle(schemaItem.title) : "Address")}
          value={field.value || ''}
          onChangeText={(text) => {
            helpers.setValue(text);
            helpers.setTouched(true);
          }}
          onFocus={() => {
            // Set focus time IMMEDIATELY and synchronously before any blur can occur
            if (Platform.OS === 'web') {
              focusTimeRef.current = getCurrentTime();
              // Start blur prevention proactively on focus for web touch devices
              // This prevents validation from running during the keyboard appearance transition
              if (isTouchDeviceRef.current) {
                blurPreventionState.startPrevention();
              }
            }
            if (focusCallback) focusCallback();
          }}
          onMouseDown={() => {
            // Set focus time even earlier - on mouse down/touch start
            if (Platform.OS === 'web' && isTouchDeviceRef.current) {
              focusTimeRef.current = getCurrentTime();
            }
          }}
          onTouchStart={() => {
            // Set focus time even earlier - on touch start
            if (Platform.OS === 'web' && isTouchDeviceRef.current) {
              focusTimeRef.current = getCurrentTime();
            }
          }}
          onBlur={(e) => {
            // On web touch devices, detect false blurs from keyboard appearance
            if (Platform.OS === 'web' && isTouchDeviceRef.current) {
              const now = getCurrentTime();
              const timeSinceFocus = now - focusTimeRef.current;
              // If blur happens within 1000ms of focus, it's likely a false blur from keyboard appearing
              if (timeSinceFocus < 1000) {
                // Start blur prevention to block validation and form remounting
                blurPreventionState.startPrevention();
                // Refocus the input to restore focus that was lost due to keyboard appearance
                // Use requestAnimationFrame to refocus after the browser has finished processing the blur
                requestAnimationFrame(() => {
                  // Small delay to ensure the blur event has fully processed
                  setTimeout(() => {
                    if (fallbackInputRef.current) {
                      try {
                        fallbackInputRef.current.focus();
                      } catch (err) {
                        console.warn('Failed to refocus input after false blur:', err);
                      }
                    }
                  }, 10);
                });
                // Don't call field.onBlur to prevent validation from running
                return;
              }
            }
            // Clear blur prevention if it was active
            blurPreventionState.stopPrevention();
            field.onBlur(schemaItem.title);
            if (blurCallback) blurCallback();
          }}
          {...(Platform.OS !== 'web' && {
            returnKeyType: index === length - 1 ? 'done' : 'next',
            onSubmitEditing: () => {
              nextFieldFunction(index, length);
              if (blurCallback) blurCallback();
            }
          })}
          style={{
            backgroundColor: bgColor,
            borderWidth: 1,
            borderColor: hasError ? '#ef4444' : theme ? '#e2e8f0' : '#374151',
            borderRadius: 12,
            paddingHorizontal: 16,
            paddingVertical: 14,
            fontSize: 16,
            color: textColor,
            minHeight: 55,
          }}
          {...(Platform.OS !== 'web' && { placeholderTextColor: placeholderColor })}
        />
      </View>
    );

    // Early return AFTER all hooks are called
    if (!isReady || !GooglePlacesComponent || loadError || runtimeError) {
      return renderFallbackInput();
    }

    return (
      <View style={{ flex: 1, marginVertical: 5, left: -5 }} onLayout={e => setWidth(e.nativeEvent.layout.width)}>
        <GooglePlacesErrorBoundary onError={() => setRuntimeError(true)}>
          {(() => {
            try {
              const Component = GooglePlacesComponent;

              if (!Component) {
                throw new Error('Component not loaded');
              }

              // Use wrapper component to prevent TDZ errors
              return (
                <SafeGooglePlacesWrapper
                  Component={Component}
                  ref={ref}
                  query={{
                    key: 'AIzaSyAznGh_h4mH0meG5V6hlTlLVhVithzlUJM',
                    language: 'en',
                    types: '(cities)',
                    components: 'country:us|country:uk|country:ca|country:au'
                  }}
                  placeholder={schemaItem.content?.label || (schemaItem.title ? formatTitle(schemaItem.title) : "Address")}
                  predefinedPlaces={[]}
                  fetchDetails={true}
                  minLength={2}
                  debounce={400}
                  onFail={(error) => {
                    console.error('GooglePlacesAutocomplete error:', error);
                    setRuntimeError(true);
                    helpers.setTouched(true, true);
                    // Don't set error message, just fallback to text input
                  }}
                  onPress={(data, details = null) => {
                    console.log('Address selected:', data, details);
                    if (data && data.description) {
                      console.log('Setting value to:', data.description);
                      helpers.setValue(data.description);
                      helpers.setTouched(true);
                      if (details?.geometry?.location) {
                        setStoredLocation(details.geometry.location);
                      }
                      updateFunction(schemaItem.title, data.description);
                      nextFieldFunction(index, length);
                    } else {
                      console.log('No description found in data:', data);
                    }
                  }}
                  GooglePlacesDetailsQuery={{
                    fields: 'formatted_address',
                  }}
                  isRowScrollable={false}
                  requestUrl={Platform.OS === 'web' && requestUrl ? requestUrl : undefined}
                  enablePoweredByContainer={false}
                  keepResultsAfterBlur={false}
                  listViewDisplayed="auto"
                  suppressDefaultStyles={false}
                  enableHighAccuracyLocation={false}
                  styles={{
                    container: {
                      flex: 1,
                      maxWidth: "100%",
                      alignSelf: "stretch",
                      zIndex: 10,
                    },
                    textInputContainer: {
                      backgroundColor: bgColor,
                      borderRadius: 12,
                      borderWidth: 1,
                      borderColor: hasError ? '#ef4444' : theme ? '#e2e8f0' : '#374151',
                    },
                    textInput: {
                      borderColor: "transparent",
                      maxHeight: undefined,
                      minHeight: 55,
                      backgroundColor: 'transparent',
                      minWidth: '100%',
                      color: textColor,
                      borderWidth: 0,
                      borderRadius: 0,
                      paddingHorizontal: 16,
                      paddingVertical: 14,
                      fontSize: 16,
                    },
                    listView: {
                      position: 'absolute',
                      top: 60,
                      left: 0,
                      right: 0,
                      backgroundColor: bgColor,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: theme ? '#e2e8f0' : '#374151',
                      maxHeight: 200,
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.1,
                      shadowRadius: 4,
                      elevation: 3,
                      zIndex: 1000,
                    },
                    row: {
                      backgroundColor: bgColor,
                      paddingHorizontal: 16,
                      paddingVertical: 12,
                      borderBottomWidth: 1,
                      borderBottomColor: theme ? '#f1f5f9' : '#334155',
                      minHeight: 44,
                      justifyContent: 'center',
                    },
                    description: {
                      color: textColor,
                      fontSize: 16,
                      fontWeight: 'bold',
                    },
                    predefinedPlacesDescription: {
                      color: placeholderColor,
                      fontSize: 16,
                    }
                  }}
                  textInputProps={{
                    onFocus: () => {
                      if (focusCallback) focusCallback();
                    },
                    onBlur: () => {
                      if (blurCallback) blurCallback();
                    },
                    ...(Platform.OS !== 'web' && {
                      placeholderTextColor: placeholderColor,
                      returnKeyType: index === length - 1 ? 'done' : 'next',
                      onSubmitEditing: () => {
                        nextFieldFunction(index, length);
                        if (blurCallback) blurCallback();
                      },
                    }),
                  }}
                />
              );
            } catch (error) {
              console.error('Error rendering GooglePlacesAutocomplete:', error);
              setRuntimeError(true);
              // Fallback to TextInput on error
              return renderFallbackInput();
            }
          })()}
        </GooglePlacesErrorBoundary>
        {
          schemaItem.content?.map && <MapView ref={mapRef} style={{ marginVertical: 10 }}>
          </MapView>
        }
      </View>
    );
  })

// HandleFieldTypeProps is now imported from types

// Debounced search helper for performance
const useDebounced = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Safe API call wrapper with error handling
const safeApiCall = async <T,>(
  apiCall: () => Promise<T>,
  setError: (error: string | null) => void,
  setLoading: (loading: boolean) => void,
  errorMessage = 'API call failed'
): Promise<T | null> => {
  try {
    setLoading(true);
    setError(null);
    const result = await apiCall();
    return result;
  } catch (error) {
    console.error(errorMessage, error);
    setError(error.message || errorMessage);
    return null;
  } finally {
    setLoading(false);
  }
};

// Generic dropdown wrapper component for consistent styling and behavior
const GenericDropdown = ({
  items,
  loading,
  error,
  value,
  onChange,
  onOpen,
  onBlur,
  placeholder,
  theme,
  hasError,
  renderItem,
  multiple = false,
  maxHeight = 350
}) => (
  <Select
    multiple={multiple}
    value={value || (multiple ? [] : '')}
    onOpen={onOpen}
    onChange={onChange}
    onBlur={onBlur}
    displayEmpty
    renderValue={(selected) => {
      if (!selected || (multiple && Array.isArray(selected) && selected.length === 0)) {
        return <em>{placeholder}</em>;
      }
      if (multiple && Array.isArray(selected)) {
        return `${selected.length} item(s) selected`;
      }
      const item = items.find(item => item.value === selected);
      return item ? item.label : selected;
    }}
    style={{
      backgroundColor: theme ? 'white' : '#333333',
      color: theme ? 'black' : 'white',
      minWidth: 180,
      marginTop: 10
    }}
    MenuProps={{
      disablePortal: false,
      container: typeof document !== 'undefined' ? document.body : undefined,
      PaperProps: {
        style: {
          backgroundColor: theme ? 'white' : '#333333',
          borderColor: hasError ? 'red' : '#4791bd',
          borderWidth: 1,
          zIndex: 99999,
          maxHeight,
        },
      },
    }}
  >
    {loading ? (
      <MenuItem disabled style={{ color: theme ? 'black' : 'white' }}>
        Loading...
      </MenuItem>
    ) : error ? (
      <MenuItem disabled style={{ color: 'orange' }}>
        {error}
      </MenuItem>
    ) : items.length === 0 ? (
      <MenuItem disabled style={{ color: theme ? 'black' : 'white' }}>
        No items found
      </MenuItem>
    ) : (
      items.map((item) => (
        <MenuItem
          key={item.value || Math.random()}
          value={item.value}
          style={{
            backgroundColor: theme ? 'white' : '#333333',
            color: theme ? 'black' : 'white'
          }}
        >
          {renderItem ? renderItem(item) : item.label}
        </MenuItem>
      ))
    )}
  </Select>
);

const HandleFieldType = (props: HandleFieldTypeProps) => {
  const {
    fieldProps: { name, onBlur, onChange, value, setFieldValues, error },
    buttonColor, blackText, schemaItem, index, length, nextFieldFunction, otherVals, errorCallback, readOnly = false, inlineFields = false
  } = props;

  const [field, meta, helpers] = useField(name);
  const [dateSelected, setDateSelected] = useState(moment());
  const [date, setDate] = useState(moment());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [city, setCity] = useState('');

  // Touch device detection for web to prevent keyboard dismissal
  const focusTimeRef = useRef<number>(0);
  const isTouchDeviceRef = useRef<boolean>(false);
  const getCurrentTime = useCallback(() => {
    if (Platform.OS === 'web' && typeof performance !== 'undefined' && performance.now) {
      return performance.now();
    }
    return Date.now();
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      isTouchDeviceRef.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }
  }, []);

  // Products state for product_select and data_items cases
  const [productsLoading, setProductsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsError, setProductsError] = useState(null);
  const [showProductsDropdown, setShowProductsDropdown] = useState(false);
  const [productsSearchText, setProductsSearchText] = useState('');

  // Initialize pickerState with proper fallback handling
  const [pickerState, setPickerState] = useState(() => {
    if (typeof value === 'object' && value !== null) {
      return value._id || value.id || value.value || value;
    }
    return value || null;
  });

  const [tags, setTags] = useState(value);
  const [open, setOpen] = React.useState(false);
  const [time, setTimeOpen] = useState(false);
  const [timeSelected, setTimeSelected] = useState(value);
  const [selectedImage, setSelectedImage] = useState(null);
  const [barcode, setBarcode] = useState(false);
  const { width } = useWindowDimensions();
  const [switchState, setSwitchState] = useState(value);
  const [localText, setLocalText] = useState(value);
  const theme = themeSelector();
  const [dateModal, setDateModal] = useState(false);
  const [height, setHeight] = useState(MIN_DROPDOWN_HIEGHT);
  const pickRef = useRef<ImagePickerHandle>(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);

  const hasError = meta.error && meta.touched;

  // Sync pickerState with field value changes
  useEffect(() => {
    if (field.value !== undefined && field.value !== pickerState) {
      if (typeof field.value === 'object' && field.value !== null) {
        setPickerState(field.value._id || field.value.id || field.value.value || field.value);
      } else {
        setPickerState(field.value);
      }
    }
  }, [field.value]);

  useEffect(()=>{
    if(hasError){
      errorCallback && errorCallback?.(index, meta.error)
    }
  },[hasError])

  const inputRef = useRef(null);

  useEffect(() => {
    props.setRef(name, inputRef);
    return () => {
      // Clean up inputRef
      inputRef.current = null;
      pickRef.current = null;
    };
  }, [name, props.setRef]);


  const handleKeyPress = (index) => {
    nextFieldFunction(index, length);
    if (props.blurCallback) props.blurCallback();
  };
  const onDismissSingle = React.useCallback(() => {
    helpers.setValue(dateSelected);
    props.blurCallback?.();
    helpers.setTouched(true, true);
    setOpen(false);
  }, [field, date, setOpen]);

  const handleImageSelected = async (img, type) => {
    setSelectedImage(img[0].uri);
    if (type === "avatar") {
      helpers.setValue({ img: img[0].base64 });
    } else {
      helpers.setValue(img[0].uri);
    }
    // Perform any additional action with the selected image uri here
  }

  const handleImagesSelected = async (images: { uri: string; base64: string }[]) => {
    return new Promise((resolve) => {
      setSelectedImages(prev => {
        const updatedImages = [...prev, ...images];
        helpers.setValue(updatedImages.map((img) => img.uri));
        resolve(true);
        return updatedImages.map((img) => img.uri);
      });
    });
  }

  const handleSwitchChange = (value) => {
    setSwitchState(value);
    helpers.setValue(value)
  }

  const handleTimeChange = (value) => {
    setTimeSelected(value);
    const { hours, minutes } = value;
    //Append time to the date selected
    const date = new Date(dateSelected);
    date.setHours(hours);
    date.setMinutes(minutes);
    helpers.setValue(date);
  }

  const onConfirmSingle = (params) => {
    helpers.setValue(params);
    helpers.setTouched(true, true);
    setDateSelected(params);
    setOpen(false);
  }

  const _helperCallback = (value) => {
    helpers.setValue(value);
  }

  const handleDateSelection = (type, date) => {
    helpers.setValue(date);
    setDateSelected(date);
    setOpen(false);
    helpers.setTouched(true, true);
  }

  const handleUpdateTags = (type) => {
    setTags(type);
    helpers.setValue(tags)
  }

  const handleUpdateCity = (type, value) => {
    helpers.setValue(value);
    helpers.setTouched(true, true);
  }

  // Special CASES
  if (schemaItem.type === 'avatar' || schemaItem.title === 'avatar') {
    return (
      <View style={{ flex: 1 }}>
        <View style={{ flexDirection: 'row', flex: 1 }}>
          {hasError && <Text style={{ color: 'red', padding: 2 }}>{meta.error}</Text>}
        </View>

        {
          field.value?.img ?
            <View style={[styles.centerEverything, { gap: 30 }]}>
              <Image source={{ uri: field.value?.img || selectedImage }} style={{ width: 100, height: 100, borderRadius: 30 }} />
              <Button icon="user" customStyle={{ minWidth: 300 }} buttonText={'Change Image'} onPress={() => { helpers.setValue(null); pickRef.current!.pickImage() }} />
            </View>
            :
            <ImagePickerComponent ref={pickRef} onImageSelected={(img) => handleImageSelected(img, "avatar")} />
        }
      </View>
    )
  }

  switch ((schemaItem.ref || schemaItem.validation?.type || schemaItem.type).toLowerCase()) {
    case 'dataset':
      return (
        <DataItemSelection changeCallback={helpers.setValue} schemaItem={schemaItem} />
      )
    case 'multicheck':
      const enumOptions = schemaItem.content?.enum || schemaItem.validation?.enum || [];
      const [checkedValues, setCheckedValues] = useState(Array.isArray(field.value) ? field.value : []);

      const handleCheckChange = (option) => {
        let updated;
        if (checkedValues.includes(option)) {
          updated = checkedValues.filter((v) => v !== option);
        } else {
          updated = [...checkedValues, option];
        }
        setCheckedValues(updated);
        helpers.setValue(updated);
      };

      return (
        <View style={{ flex: 1, marginVertical: 10, zIndex: 999999, position: 'relative' }}>
          {/* Error display */}
          {hasError && (
            <Text

              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}


          {/* Modern Multi-Checkbox Container */}
          <View style={{
            backgroundColor: theme ? 'white' : '#1e293b',
            borderWidth: 1,
            borderColor: hasError ? '#ef4444' : theme ? '#e2e8f0' : '#374151',
            borderRadius: 12,
            padding: 16,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 999999,
            zIndex: 999999,
            position: 'relative'
          }}>
            {enumOptions.map((option, index) => (
              <Pressable
                key={option}
                onPress={() => !readOnly && handleCheckChange(option)}
                disabled={readOnly}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingVertical: 12,
                  borderBottomWidth: index < enumOptions.length - 1 ? 1 : 0,
                  borderBottomColor: theme ? '#f1f5f9' : '#334155',
                }}
              >
                <Checkbox
                  value={checkedValues.includes(option)}
                  onValueChange={() => !readOnly && handleCheckChange(option)}
                  disabled={readOnly}
                  style={{
                    marginRight: 12,
                    transform: [{ scale: 1.1 }]
                  }}
                />
                <Text style={{
                  fontSize: 16,
                  color: theme ? '#1e293b' : '#f8fafc',
                  fontWeight: checkedValues.includes(option) ? '600' : '400',
                  flex: 1
                }}>
                  {toTitleCase(option)}
                </Text>
              </Pressable>
            ))}
          </View>

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}

          {/* Selection count */}
          {checkedValues.length > 0 && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#0369a1' : '#60a5fa',
              marginTop: 3,
              fontWeight: '500'
            }}>
              {checkedValues.length} option{checkedValues.length !== 1 ? 's' : ''} selected
            </Text>
          )}
        </View>
      );
    case 'object':
    case 'group':
    case 'Object':
      const [isExpanded, setIsExpanded] = useState(schemaItem.content?.defaultExpanded || false);
      const objectTitle = schemaItem.content?.label || (schemaItem.title ? formatTitle(schemaItem.title) : 'Object');
      const objectHasError = hasError || (meta.error && meta.touched);

      // Get nested fields - check multiple possible locations
      const nestedFields = React.useMemo(() => {
        return (schemaItem as any).fields ||
          (schemaItem as any).content?.fields ||
          (schemaItem as any).children ||
          [];
      }, [schemaItem]);

      // Get nested field errors
      const nestedErrors = React.useMemo(() => {
        if (!meta.error || typeof meta.error !== 'object') return null;
        return meta.error;
      }, [meta.error]);

      // Check if any nested field has an error
      const hasNestedErrors = React.useMemo(() => {
        if (!nestedErrors) return false;
        return Object.keys(nestedErrors).some(key => nestedErrors[key]);
      }, [nestedErrors]);

      return (
        <View style={{
          flex: 1,
          marginVertical: 10,
          zIndex: 999999,
          position: 'relative'
        }}>
          {/* Error display */}
          {objectHasError && typeof meta.error === 'string' && (
            <Text
              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

          {/* Modern Object/Group Container */}
          <View style={{
            backgroundColor: theme ? 'white' : '#1e293b',
            borderWidth: 1,
            borderColor: objectHasError || hasNestedErrors ? '#ef4444' : theme ? '#e2e8f0' : '#374151',
            borderRadius: 12,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 999999,
            zIndex: 999999,
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Header with Expand/Collapse */}
            <Pressable
              onPress={() => !readOnly && setIsExpanded(!isExpanded)}
              disabled={readOnly}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingHorizontal: 16,
                paddingVertical: 14,
                minHeight: 50,
                backgroundColor: isExpanded
                  ? (theme ? '#f8fafc' : '#0f172a')
                  : 'transparent',
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                <View style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  backgroundColor: theme ? '#f1f5f9' : '#334155',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 12
                }}>
                  <AntDesign
                    name={isExpanded ? "caret-up" : "caret-down"}
                    size={14}
                    color={theme ? '#475569' : '#94a3b8'}
                  />
                </View>
                <Text style={{
                  fontSize: 16,
                  fontWeight: '600',
                  color: theme ? '#1e293b' : '#f8fafc',
                  flex: 1
                }}>
                  {objectTitle}
                </Text>
                {schemaItem.content?.validation?.required && (
                  <Text style={{
                    fontSize: 12,
                    color: '#ef4444',
                    marginLeft: 8
                  }}>
                    *
                  </Text>
                )}
              </View>

              {/* Error indicator */}
              {(objectHasError || hasNestedErrors) && (
                <View style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#ef4444',
                  marginLeft: 8
                }} />
              )}

              {/* Field count indicator */}
              {nestedFields && nestedFields.length > 0 && (
                <Text style={{
                  fontSize: 12,
                  color: theme ? '#64748b' : '#94a3b8',
                  marginLeft: 8,
                  fontWeight: '500'
                }}>
                  {nestedFields.length} field{nestedFields.length !== 1 ? 's' : ''}
                </Text>
              )}
            </Pressable>

            {/* Separator */}
            {isExpanded && (
              <View style={{
                height: 1,
                backgroundColor: theme ? '#e2e8f0' : '#374151',
                marginHorizontal: 16
              }} />
            )}

            {/* Nested Fields */}
            {isExpanded && nestedFields && nestedFields.length > 0 && (
              <View style={{
                paddingHorizontal: Platform.OS === 'web' ? 20 : 16,
                paddingVertical: 16,
                backgroundColor: theme ? '#fafafa' : '#0f172a',
                ...(inlineFields && Platform.OS === 'web' ? { minWidth: '90dvw' as any } : { minWidth: '100%' }),
              }}>
                {nestedFields.map((nestedField: any, nestedIndex: number) => {
                  // Construct proper field name using dot notation
                  const baseFieldName = (schemaItem as any).title || (schemaItem as any).name || (schemaItem as any).key || 'object';
                  const nestedFieldName = nestedField?.title || nestedField?.name || nestedField?.key || `field${nestedIndex}`;
                  const fullFieldName = `${baseFieldName}.${nestedFieldName}`;

                  // Get nested field error if exists
                  const nestedFieldError = nestedErrors && nestedErrors[nestedFieldName];

                  return (
                    <View
                      key={nestedFieldName || nestedIndex}
                      style={{
                        marginBottom: nestedIndex < nestedFields.length - 1 ? 16 : 0,
                        ...(inlineFields ? { maxWidth: 400, minWidth: 300 } : {}),
                      }}
                    >
                      {/* Nested field error display */}
                      {nestedFieldError && (
                        <Text style={{
                          color: '#ef4444',
                          fontSize: 11,
                          marginBottom: 4,
                          paddingLeft: 4
                        }}>
                          {typeof nestedFieldError === 'string' ? nestedFieldError : 'Invalid value'}
                        </Text>
                      )}

                      <FastField
                        name={fullFieldName}
                        validateOnChange
                        validateOnBlur
                      >
                        {(fieldProps: FieldProps) => {
                          return (
                            <ErrorBoundary id={`nested-field-${fullFieldName}`}>
                              <GetField
                                otherVals={otherVals}
                                item={nestedField}
                                field={fieldProps.field}
                                allItems={nestedFields}
                                index={nestedIndex}
                                nextFieldFunction={nextFieldFunction}
                                setInputRef={props.setRef}
                                fieldProps={fieldProps}
                                blackText={blackText}
                                hideLabel={false}
                                inlineFields={false}
                              />
                            </ErrorBoundary>
                          );
                        }}
                      </FastField>
                    </View>
                  );
                })}
              </View>
            )}

            {/* Empty state */}
            {isExpanded && (!nestedFields || nestedFields.length === 0) && (
              <View style={{
                padding: 20,
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Text style={{
                  fontSize: 14,
                  color: theme ? '#64748b' : '#94a3b8',
                  fontStyle: 'italic'
                }}>
                  No fields defined for this object
                </Text>
                {/* Debug info - remove in production */}
                {__DEV__ && (
                  <Text style={{
                    fontSize: 10,
                    color: theme ? '#94a3b8' : '#64748b',
                    marginTop: 8,
                    fontFamily: 'monospace'
                  }}>
                    Debug: schemaItem keys: {Object.keys(schemaItem as any).join(', ')}
                    {'\n'}Has fields: {(schemaItem as any).fields ? 'yes' : 'no'}
                    {'\n'}Has content.fields: {(schemaItem as any).content?.fields ? 'yes' : 'no'}
                    {'\n'}Fields count: {nestedFields?.length || 0}
                  </Text>
                )}
              </View>
            )}
          </View>

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic',
              paddingLeft: 4
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}

          {/* Nested errors summary */}
          {hasNestedErrors && isExpanded && (
            <Text style={{
              fontSize: 11,
              color: '#ef4444',
              marginTop: 5,
              paddingLeft: 4,
              fontWeight: '500'
            }}>
              Please fix errors in nested fields above
            </Text>
          )}
        </View>
      )
    case 'country':
      // Add fallback for country list
      const safeCountryList = Array.isArray(countryList) && countryList.length > 0 ? countryList : [
        { label: 'United States', value: 'US' },
        { label: 'United Kingdom', value: 'UK' },
        { label: 'Canada', value: 'CA' },
        { label: 'Australia', value: 'AU' }
      ];

      // Handle fallback for empty country list
      if (!safeCountryList || safeCountryList.length === 0) {
        return (
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row' }}>
              <Text style={{ color: 'orange', padding: 2 }}>
                No countries available
              </Text>
            </View>
          </View>
        );
      }

      // Sync picker state with field value
      React.useEffect(() => {
        if (field.value !== undefined && field.value !== pickerState) {
          setPickerState(field.value);
        }
      }, [field.value]);

      const [showCountryDropdown, setShowCountryDropdown] = useState(false);
      const selectedCountry = safeCountryList.find(country => country.value === pickerState);

      return (
        <View style={{
          flex: 1,
          marginVertical: 10,
          zIndex: showCountryDropdown ? 10000 : 1,
          elevation: showCountryDropdown ? 10000 : 1
        }}>
          {/* Error display */}
          {hasError && (
            <Text

              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

          {/* Modern Country Dropdown */}
          <View style={{ position: 'relative', zIndex: showCountryDropdown ? 10001 : 1 }}>
            <Pressable
              onPress={() => setShowCountryDropdown(!showCountryDropdown)}
              style={{
                backgroundColor: theme ? 'white' : '#1e293b',
                borderWidth: 1,
                borderColor: hasError ? '#ef4444' : (showCountryDropdown ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
                borderRadius: 12,
                paddingHorizontal: 16,
                paddingVertical: 14,
                minHeight: 50,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 3,
              }}
            >
              <Text style={{
                color: selectedCountry ? (theme ? '#1e293b' : '#f8fafc') : (theme ? '#64748b' : '#94a3b8'),
                fontSize: 16,
                fontWeight: selectedCountry ? '600' : '400',
                flex: 1
              }}>
                {selectedCountry ? selectedCountry.label : "Select a country..."}
              </Text>

              <View
                animation={showCountryDropdown ? 'rotate' : undefined}
                duration={200}
              >
                <FontAwesome
                  name={showCountryDropdown ? 'chevron-up' : 'chevron-down'}
                  size={14}
                  color={theme ? '#64748b' : '#94a3b8'}
                />
              </View>
            </Pressable>

            {/* Dropdown Options */}
            {showCountryDropdown && (
              <View
                animation="fadeInDown"
                duration={200}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: theme ? 'white' : '#1e293b',
                  borderWidth: 1,
                  borderColor: theme ? '#e2e8f0' : '#374151',
                  borderRadius: 12,
                  marginTop: 4,
                  maxHeight: 300,
                  zIndex: 10002,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.15,
                  shadowRadius: 12,
                  elevation: 10002,
                }}
              >
                <ScrollView
                  style={{ maxHeight: 300 }}
                  showsVerticalScrollIndicator={false}
                  nestedScrollEnabled
                >
                  {safeCountryList.map((country, index) => (
                    <Pressable
                      key={country.value || country.label || index}
                      onPress={() => {
                        setPickerState(country.value);
                        helpers.setValue(country.value);
                        helpers.setTouched(true);
                        setShowCountryDropdown(false);
                      }}
                      style={({ pressed }) => ({
                        paddingHorizontal: 16,
                        paddingVertical: 14,
                        backgroundColor: pressed
                          ? (theme ? '#f1f5f9' : '#334155')
                          : (pickerState === country.value
                            ? (theme ? '#e0f2fe' : '#1e40af20')
                            : 'transparent'),
                        borderBottomWidth: index < safeCountryList.length - 1 ? 1 : 0,
                        borderBottomColor: theme ? '#f1f5f9' : '#334155',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      })}
                    >
                      <Text style={{
                        color: pickerState === country.value
                          ? (theme ? '#0369a1' : '#60a5fa')
                          : (theme ? '#1e293b' : '#f8fafc'),
                        fontSize: 16,
                        fontWeight: pickerState === country.value ? '600' : '400',
                        flex: 1
                      }}>
                        {country.label}
                      </Text>

                      {pickerState === country.value && (
                        <FontAwesome
                          name="check"
                          size={14}
                          color={theme ? '#0369a1' : '#60a5fa'}
                        />
                      )}
                    </Pressable>
                  ))}
                </ScrollView>
              </View>
            )}

            {/* Backdrop to close dropdown */}
            {showCountryDropdown && (
              <Pressable
                onPress={() => setShowCountryDropdown(false)}
                style={{
                  position: 'absolute',
                  top: -1000,
                  left: -1000,
                  right: -1000,
                  bottom: -1000,
                  zIndex: 10000,
                }}
              />
            )}
          </View>

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}
        </View>
      )
    case 'timeslider':
      const [timeValue, setTimeValue] = useState([9, 17]);
      const [timeValue2, setTimeValue2] = useState(value);

      const handleChange2 = (event, newValue) => {
        setTimeValue(newValue);
        helpers.setValue(newValue);
      };

      return (
        <View style={{ gap: 10 }} >
          <MatSlider
            getAriaLabel={() => 'Minimum distance shift'}
            value={timeValue}
            step={1}
            max={24}
            color={'primary'}
            min={0}
            onChange={handleChange2}
            valueLabelDisplay="on"
            valueLabelFormat={(value) => {
              return value + ":00";
            }}
            marks={true}
            // getAriaValueText={"valuetext"}
            disableSwap
          />
        </View>
      )
    case 'slider':
      const [sliderValue, setSliderValue] = useState(value);
      const maxValue = schemaItem.content?.maxValue || 100;
      const minValue = schemaItem.content?.minValue || 0;
      const steps = schemaItem.content?.steps || 1;
      const stepList = Array.from({ length: maxValue / steps }, (_, i) => i * steps);

      function getHalfPoint(value) {
        return value / 2;
      }
      return (
        <View  style={{ width: "90%", maxWidth: "90%", alignSelf: "stretch", flex: 1, }}>
          <View style={{ flexDirection: 'row' }}>
            {hasError && <Text style={{ color: 'red', padding: 2 }}>{meta.error}</Text>}
          </View>

          <Slider valueLabelFormat={(value) => {
            if (schemaItem.validation?.enum) {
              return schemaItem.validation?.enum[value];
            } else {
              return ("")
            }
          }}
            min={schemaItem.validation?.minValue || 0} max={schemaItem.validation?.maxValue || 1} step={schemaItem.validation?.step || 1} valueLabelDisplay="on" style={{ paddingTop: 30, flex: 1, minWidth: 180, minHeight: 70, left: -10, alignContent: "flex-start" }} value={field.value} onChange={(event, value) => {
              helpers.setValue(value);
            }} />
        </View>
      )
    case 'product_select':
    case 'data_items':

      const handleProductOpen = async () => {
        setProductsLoading(true);
        setProductsError(null);
        try {
          const apis = await getApis();
          const res = await apis.getProductServiceList();
          if (res && Array.isArray(res)) {
            // Transform the API response to match expected format
            const transformedProducts = res.map(item => ({
              ...item,
              label: item.data?.product_name || item.name || item.label || 'Unknown Product',
              value: item._id || item.id || item.value,
              description: item.data?.product_description || item.description
            }));
            setProducts(transformedProducts);
          } else {
            setProducts([]);
            setProductsError('Invalid response format');
          }
        } catch (error) {
          console.error('Error loading products:', error);
          setProducts([]);
          setProductsError('Failed to load products');
        } finally {
          setProductsLoading(false);
        }
      }


      // Sync picker state with field value for multiple selection
      React.useEffect(() => {
        if (Array.isArray(field.value)) {
          const fieldValueString = JSON.stringify(field.value);
          const pickerStateString = JSON.stringify(pickerState);
          if (fieldValueString !== pickerStateString) {
            setPickerState(field.value);
          }
        }
      }, [field.value]);

      const handleProductToggle = (productId) => {
        const currentValues = Array.isArray(pickerState) ? pickerState : [];
        let updatedValues;

        if (currentValues.includes(productId)) {
          updatedValues = currentValues.filter(id => id !== productId);
        } else {
          updatedValues = [...currentValues, productId];
        }

        setPickerState(updatedValues);
        helpers.setValue(updatedValues);
        helpers.setTouched(true);
      };

      const getSelectedProductsText = () => {
        const selectedCount = Array.isArray(pickerState) ? pickerState.length : 0;
        if (selectedCount === 0) return "Select data items...";
        return `${selectedCount} item${selectedCount !== 1 ? 's' : ''} selected`;
      };

      const filteredProducts = React.useMemo(() => {
        if (!productsSearchText || productsSearchText.trim().length === 0) return products;

        const filtered = products?.filter?.(item =>
          item?.label?.toLowerCase().includes(productsSearchText.toLowerCase()) ||
          item?.description?.toLowerCase().includes(productsSearchText.toLowerCase()) ||
          item?.data?.product_name?.toLowerCase().includes(productsSearchText.toLowerCase()) ||
          item?.data?.product_description?.toLowerCase().includes(productsSearchText.toLowerCase())
        ) || [];
        return filtered;
      }, [productsSearchText, products]);

      return (
        <View style={{
          flex: 1,
          marginVertical: 10,
          zIndex: showProductsDropdown ? 10000 : 1,
          elevation: showProductsDropdown ? 10000 : 1
        }}>
          {/* Error display */}
          {hasError && (
            <Text

              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

          {/* Products Error display */}
          {productsError && (
            <Text

              style={{ color: 'orange', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {productsError}
            </Text>
          )}


          {/* Modern Multi-select Container */}
          <View style={{ position: 'relative', zIndex: showProductsDropdown ? 10001 : 1 }}>
            <Pressable
              onPress={async () => {
                if (!showProductsDropdown && products.length === 0) {
                  await handleProductOpen();
                }
                setShowProductsDropdown(!showProductsDropdown);
              }}
              style={{
                backgroundColor: theme ? 'white' : '#1e293b',
                borderWidth: 1,
                borderColor: hasError ? '#ef4444' : (showProductsDropdown ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
                borderRadius: 12,
                paddingHorizontal: 16,
                paddingVertical: 14,
                minHeight: 50,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 3,
              }}
            >
              <Text style={{
                color: (Array.isArray(pickerState) && pickerState.length > 0)
                  ? (theme ? '#1e293b' : '#f8fafc')
                  : (theme ? '#64748b' : '#94a3b8'),
                fontSize: 16,
                fontWeight: (Array.isArray(pickerState) && pickerState.length > 0) ? '600' : '400',
                flex: 1
              }}>
                {getSelectedProductsText()}
              </Text>

              <View
                animation={showProductsDropdown ? 'rotate' : undefined}
                duration={200}
              >
                <FontAwesome
                  name={productsLoading ? 'refresh' : (showProductsDropdown ? 'chevron-up' : 'chevron-down')}
                  size={14}
                  color={theme ? '#64748b' : '#94a3b8'}
                />
              </View>
            </Pressable>

            {/* Dropdown Options */}
            {showProductsDropdown && (
              <View
                animation="fadeInDown"
                duration={200}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: theme ? 'white' : '#1e293b',
                  borderWidth: 1,
                  borderColor: theme ? '#e2e8f0' : '#374151',
                  borderRadius: 12,
                  marginTop: 4,
                  maxHeight: 350,
                  zIndex: 10002,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.15,
                  shadowRadius: 12,
                  elevation: 10002,
                }}
              >
                {/* Search Input */}
                <View style={{ padding: 12, borderBottomWidth: 1, borderBottomColor: theme ? '#f1f5f9' : '#334155' }}>
                  <TextInput
                    value={productsSearchText}
                    onChangeText={setProductsSearchText}
                    placeholder="Search data items..."
                    {...(Platform.OS !== 'web' && { placeholderTextColor: theme ? '#64748b' : '#94a3b8' })}
                    style={{
                      backgroundColor: theme ? '#f8fafc' : '#0f172a',
                      borderWidth: 1,
                      borderColor: theme ? '#e2e8f0' : '#475569',
                      borderRadius: 8,
                      paddingHorizontal: 12,
                      paddingVertical: 8,
                      fontSize: 14,
                      color: theme ? '#1e293b' : '#f8fafc',
                    }}
                  />
                </View>

                <ScrollView
                  style={{ maxHeight: 280 }}
                  showsVerticalScrollIndicator={false}
                  nestedScrollEnabled
                >
                  {productsLoading ? (
                    <View style={{ padding: 16, alignItems: 'center' }}>
                      <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                        Loading data items...
                      </Text>
                    </View>
                  ) : productsError ? (
                      <View style={{ padding: 16, alignItems: 'center' }}>
                        <Text style={{ color: '#ef4444', fontSize: 14 }}>
                          {productsError}
                        </Text>
                      </View>
                    ) : filteredProducts.length === 0 ? (
                      <View style={{ padding: 16, alignItems: 'center' }}>
                        <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                          {productsSearchText ? 'No matching items found' : 'No data items available'}
                        </Text>
                      </View>
                    ) : (
                          filteredProducts.map((product, index) => {
                            const productId = product.value || product._id;
                            const isSelected = Array.isArray(pickerState) && pickerState.includes(productId);

                            return (
                              <Pressable
                                key={productId || Math.random()}
                                onPress={() => handleProductToggle(productId)}
                                style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            paddingHorizontal: 16,
                            paddingVertical: 12,
                            borderBottomWidth: index < filteredProducts.length - 1 ? 1 : 0,
                            borderBottomColor: theme ? '#f1f5f9' : '#334155',
                            backgroundColor: isSelected
                              ? (theme ? '#eff6ff' : '#1e3a8a')
                              : 'transparent',
                          }}
                        >
                          <Checkbox
                            value={isSelected}
                            onValueChange={() => handleProductToggle(productId)}
                            style={{
                              marginRight: 12,
                              transform: [{ scale: 1.1 }]
                            }}
                          />
                          <View style={{ flex: 1 }}>
                                  <Text style={{
                                    fontSize: 16,
                                    color: theme ? '#1e293b' : '#f8fafc',
                                    fontWeight: '500'
                                  }}>
                                    {product.label || product.data?.product_name || 'Unknown Product'}
                                  </Text>
                                  {product.description && (
                                    <Text style={{
                                      fontSize: 14,
                                      color: theme ? '#64748b' : '#94a3b8',
                                      marginTop: 2
                                    }}>
                                      {product.description}
                                    </Text>
                                  )}
                          </View>
                        </Pressable>
                      );
                    })
                  )}
                </ScrollView>
              </View>
            )}

            {/* Backdrop to close dropdown */}
            {showProductsDropdown && (
              <Pressable
                onPress={() => setShowProductsDropdown(false)}
                style={{
                  position: 'absolute',
                  top: -1000,
                  left: -1000,
                  right: -1000,
                  bottom: -1000,
                  zIndex: 10000,
                }}
              />
            )}
          </View>

          {/* Selection summary */}
          {Array.isArray(pickerState) && pickerState.length > 0 && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#0369a1' : '#60a5fa',
              marginTop: 5,
              fontWeight: '500'
            }}>
              {pickerState.length} data item{pickerState.length !== 1 ? 's' : ''} selected
            </Text>
          )}

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}
        </View>
      );
    case 'sign':
      return (
        <SignComponent {...props} />
      )
    case 'template_select':
      const [templatesLoading, setTemplatesLoading] = useState(false);
      const [templates, setTemplates] = useState([]);
      const [templatesError, setTemplatesError] = useState(null);
      const [showTemplatesDropdown, setShowTemplatesDropdown] = useState(false);
      const [templatesSearchText, setTemplatesSearchText] = useState('');

      const handleTemplateOpen = async () => {
        setTemplatesLoading(true);
        setTemplatesError(null);
        try {
          const apis = await getApis();
          const res = await apis.getTemplates();
          if (res && res.templates && Array.isArray(res.templates)) {
            const formattedTemplates = res.templates.map(template => ({
              ...template,
              label: template.name || template.label || template.title || 'Unnamed Template',
              value: template._id || template.id || template.value
            })).filter(template => template.value); // Filter out templates without valid IDs

            setTemplates(formattedTemplates);
          } else {
            setTemplates([]);
            setTemplatesError('Invalid response format');
          }
        } catch (error) {
          console.error('Error loading templates:', error);
          setTemplates([]);
          setTemplatesError('Failed to load templates');
        } finally {
          setTemplatesLoading(false);
        }
      }

      // Sync picker state with field value
      React.useEffect(() => {
        if (field.value !== undefined && field.value !== pickerState) {
          setPickerState(field.value);
        }
      }, [field.value]);

      const filteredTemplates = React.useMemo(() => {
        if (!templatesSearchText || templatesSearchText.trim().length === 0) return templates;

        const filtered = templates?.filter?.(item =>
          item?.label?.toLowerCase().includes(templatesSearchText.toLowerCase()) ||
          item?.name?.toLowerCase().includes(templatesSearchText.toLowerCase()) ||
          item?.title?.toLowerCase().includes(templatesSearchText.toLowerCase())
        ) || [];
        return filtered;
      }, [templatesSearchText, templates]);

      const selectedTemplate = templates.find(t => t.value === pickerState);

      return (
        <View style={{
          flex: 1,
          marginVertical: 10,
          zIndex: showTemplatesDropdown ? 10000 : 1,
          elevation: showTemplatesDropdown ? 10000 : 1
        }}>
          {/* Error display */}
          {hasError && (
            <Text
              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

          {/* Templates Error display */}
          {templatesError && (
            <Text
              style={{ color: 'orange', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {templatesError}
            </Text>
          )}

          {/* Template Preview Image */}
          {selectedTemplate?.snapShot && (
            <ErrorBoundary>
              <Image
                source={{ uri: selectedTemplate.snapShot }}
                style={[styles.centerEverything, { width: 200, height: 200, borderRadius: 10, marginVertical: 10 }]}
              />
            </ErrorBoundary>
          )}

          {/* Modern Template Dropdown Container */}
          <View style={{ position: 'relative', zIndex: showTemplatesDropdown ? 10001 : 1 }}>
            <Pressable
              onPress={async () => {
                if (!showTemplatesDropdown && templates.length === 0) {
                  await handleTemplateOpen();
                }
                setShowTemplatesDropdown(!showTemplatesDropdown);
              }}
              style={{
                backgroundColor: theme ? 'white' : '#1e293b',
                borderWidth: 1,
                borderColor: hasError ? '#ef4444' : (showTemplatesDropdown ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
                borderRadius: 12,
                paddingHorizontal: 16,
                paddingVertical: 14,
                minHeight: 50,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 3,
              }}
            >
              <Text style={{
                color: selectedTemplate ? (theme ? '#1e293b' : '#f8fafc') : (theme ? '#64748b' : '#94a3b8'),
                fontSize: 16,
                fontWeight: selectedTemplate ? '600' : '400',
                flex: 1
              }}>
                {selectedTemplate ? selectedTemplate.label : "Select a template..."}
              </Text>

              <View
                animation={showTemplatesDropdown ? 'rotate' : undefined}
                duration={200}
              >
                <FontAwesome
                  name={templatesLoading ? 'refresh' : (showTemplatesDropdown ? 'chevron-up' : 'chevron-down')}
                  size={14}
                  color={theme ? '#64748b' : '#94a3b8'}
                />
              </View>
            </Pressable>

            {/* Dropdown Options */}
            {showTemplatesDropdown && (
              <View
                animation="fadeInDown"
                duration={200}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: theme ? 'white' : '#1e293b',
                  borderWidth: 1,
                  borderColor: theme ? '#e2e8f0' : '#374151',
                  borderRadius: 12,
                  marginTop: 4,
                  maxHeight: 400,
                  zIndex: 10002,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.15,
                  shadowRadius: 12,
                  elevation: 10002,
                }}
              >
                {/* Search Input */}
                <View style={{ padding: 12, borderBottomWidth: 1, borderBottomColor: theme ? '#f1f5f9' : '#334155' }}>
                  <TextInput
                    value={templatesSearchText}
                    onChangeText={setTemplatesSearchText}
                    placeholder="Search templates..."
                    {...(Platform.OS !== 'web' && { placeholderTextColor: theme ? '#64748b' : '#94a3b8' })}
                    style={{
                      backgroundColor: theme ? '#f8fafc' : '#0f172a',
                      borderWidth: 1,
                      borderColor: theme ? '#e2e8f0' : '#475569',
                      borderRadius: 8,
                      paddingHorizontal: 12,
                      paddingVertical: 8,
                      fontSize: 14,
                      color: theme ? '#1e293b' : '#f8fafc',
                    }}
                  />
                </View>

                <ScrollView
                  style={{ maxHeight: 330 }}
                  showsVerticalScrollIndicator={false}
                  nestedScrollEnabled
                >
                  {templatesLoading ? (
                    <View style={{ padding: 16, alignItems: 'center', minHeight: 50 }}>
                      <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                        Loading templates...
                      </Text>
                    </View>
                  ) : templatesError ? (
                      <View style={{ padding: 16, alignItems: 'center', minHeight: 50 }}>
                        <Text style={{ color: '#ef4444', fontSize: 14 }}>
                          {templatesError}
                        </Text>
                      </View>
                    ) : filteredTemplates.length === 0 ? (
                      <View style={{ padding: 16, alignItems: 'center', minHeight: 50 }}>
                        <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                          {templatesSearchText ? 'No matching templates found' : 'No templates available'}
                        </Text>
                      </View>
                    ) : (
                          filteredTemplates.map((template, index) => {
                            const isSelected = pickerState === template.value;

                            return (
                              <Pressable
                                key={template.value || Math.random()}
                          onPress={() => {
                            setPickerState(template.value);
                            helpers.setValue(template.value);
                            helpers.setTouched(true);
                            setShowTemplatesDropdown(false);
                          }}
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            paddingHorizontal: 16,
                            paddingVertical: 12,
                            minHeight: 100,
                            borderBottomWidth: index < filteredTemplates.length - 1 ? 1 : 0,
                            borderBottomColor: theme ? '#f1f5f9' : '#334155',
                            backgroundColor: isSelected
                              ? (theme ? '#eff6ff' : '#1e3a8a')
                              : 'transparent',
                          }}
                        >
                          {/* Template Preview Thumbnail */}
                          {template.snapShot && (
                            <Image
                              source={{ uri: template.snapShot }}
                              style={{
                                width: 60,
                                height: 80,
                                borderRadius: 6,
                                marginRight: 12,
                                backgroundColor: theme ? '#f1f5f9' : '#334155'
                              }}
                              resizeMode="cover"
                            />
                          )}

                          <View style={{ flex: 1 }}>
                            <Text style={{
                              fontSize: 16,
                              color: theme ? '#1e293b' : '#f8fafc',
                              fontWeight: '600',
                              marginBottom: 4
                            }}>
                              {template.label || template.name || 'Unnamed Template'}
                            </Text>
                            {template.description && (
                              <Text style={{
                                fontSize: 14,
                                color: theme ? '#64748b' : '#94a3b8',
                                marginTop: 2
                              }}>
                                {template.description}
                              </Text>
                            )}
                            {template.category && (
                              <Text style={{
                                fontSize: 12,
                                color: theme ? '#94a3b8' : '#64748b',
                                marginTop: 4,
                                fontStyle: 'italic'
                              }}>
                                {template.category}
                              </Text>
                            )}
                          </View>

                          {/* Selected indicator */}
                          {isSelected && (
                            <FontAwesome
                              name="check"
                              size={16}
                              color={theme ? '#0369a1' : '#60a5fa'}
                              style={{ marginLeft: 8 }}
                            />
                          )}
                        </Pressable>
                      );
                    })
                  )}
                </ScrollView>
              </View>
            )}

            {/* Backdrop to close dropdown */}
            {showTemplatesDropdown && (
              <Pressable
                onPress={() => setShowTemplatesDropdown(false)}
                style={{
                  position: 'absolute',
                  top: -1000,
                  left: -1000,
                  right: -1000,
                  bottom: -1000,
                  zIndex: 10000,
                }}
              />
            )}
          </View>

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}
        </View>
      )
    case 'crm_item_select':
      const [crmItemsLoading, setCRMItemsLoading] = useState(false);
      const [crmItems, setCRMItems] = useState([]);
      const [crmError, setCrmError] = useState(null);

      const handleCRMItemOpen = async () => {
        setCRMItemsLoading(true);
        setCrmError(null);
        setHeight(350);
        try {
          const apis = await getApis();
          const res = await apis.getCrmItems(otherVals?._id);
          if (res && Array.isArray(res)) {
            // Convert CRM items to the format required by the dropdown picker
            const formattedItems = res.map((item) => {
              if (item && item.data) {
                const name = item.data.name || '';
                const surname = item.data.surname || '';
                const displayName = name && surname ? `${name} ${surname}` : name || surname || 'Unnamed Item';
                return {
                  ...item,
                  label: displayName,
                  value: item._id || item.id
                };
              }
              return {
                ...item,
                label: 'Invalid Item',
                value: item._id || item.id || Math.random()
              };
            }).filter(item => item.value); // Filter out items without valid IDs

            setCRMItems(formattedItems);
          } else {
            setCRMItems([]);
            setCrmError('Invalid response format');
          }
        } catch (error) {
          console.error('Error loading CRM items:', error);
          setCRMItems([]);
          setCrmError('Failed to load CRM items');
        } finally {
          setCRMItemsLoading(false);
        }
      }

      const handleCRMItemSearch = async (text) => {
        if (!text || text.trim().length === 0) return;

        setCRMItemsLoading(true);
        try {
          const filtered = crmItems?.filter?.(item =>
            item?.label?.toLowerCase().includes(text.toLowerCase())
          ) || [];
          setCRMItems(filtered);
        } catch (error) {
          console.error('Error filtering CRM items:', error);
          setCrmError('Error filtering items');
        } finally {
          setCRMItemsLoading(false);
        }
      }

      return (
        <View style={{ flex: 1, alignContent: "flex-start", justifyContent: "flex-start", marginBottom: height }}>
          <View style={{ flexDirection: 'row' }}>
            {hasError && (
              <Text style={{ color: 'red', padding: 2 }}>
                {meta.error}
              </Text>
            )}
            {crmError && (
              <Text style={{ color: 'orange', padding: 2 }}>
                {crmError}
              </Text>
            )}
          </View>
          <Select
            value={pickerState || ''}
            onOpen={handleCRMItemOpen}
            onChange={(event) => {
              const selectedValue = event.target.value;
              setPickerState(selectedValue);
              helpers.setValue(selectedValue);
              helpers.setTouched(true);
            }}
            onBlur={onBlur}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return <em>Select CRM Item</em>;
              }
              const item = crmItems.find(item => item.value === selected);
              return item ? item.label : selected;
            }}
            style={{
              backgroundColor: theme ? 'white' : '#333333',
              color: theme ? 'black' : 'white',
              minWidth: 180,
              marginTop: 10
            }}
            MenuProps={{
              disablePortal: false,
              container: typeof document !== 'undefined' ? document.body : undefined,
              PaperProps: {
                style: {
                  backgroundColor: theme ? 'white' : '#333333',
                  borderColor: '#4791bd',
                  borderWidth: 1,
                  zIndex: 99999,
                  maxHeight: 350,
                },
              },
            }}
          >
            {crmItemsLoading ? (
              <MenuItem disabled style={{ color: theme ? 'black' : 'white' }}>
                Loading CRM items...
              </MenuItem>
            ) : crmError ? (
              <MenuItem disabled style={{ color: 'orange' }}>
                {crmError}
              </MenuItem>
            ) : crmItems.length === 0 ? (
                  <MenuItem disabled style={{ color: theme ? 'black' : 'white' }}>
                    No CRM items found
                  </MenuItem>
            ) : (
              crmItems.map((item) => (
                <MenuItem
                  key={item.value || Math.random()}
                  value={item.value}
                  style={{
                    backgroundColor: theme ? 'white' : '#333333',
                    color: theme ? 'black' : 'white'
                  }}
                >
                  <TemplateListItem icon="user" {...item} />
                </MenuItem>
              ))
            )}
          </Select>
        </View>
      )
    case 'customer_datas':
      const [crmLoading, setCrmLoading] = useState(false);
      const [crmList, setCRMList] = useState([]);
      const [crmListError, setCrmListError] = useState(null);
      const [showCrmDropdown, setShowCrmDropdown] = useState(false);
      const [crmSearchText, setCrmSearchText] = useState('');

      const handleCRMOpen = async () => {
        setCrmLoading(true);
        setCrmListError(null);
        try {
          const apis = await getApis();
          const res = await apis.getCRMFolders();
          if (res && res.dataSets && Array.isArray(res.dataSets)) {
            // Format the CRM data to match dropdown structure
            const formattedData = res.dataSets.map(item => ({
              ...item,
              label: item.label || item.name || 'Unnamed Item',
              value: item.value || item._id || item.id
            })).filter(item => item.value);
            setCRMList(formattedData);
          } else {
            setCRMList([]);
            setCrmListError('Invalid response format');
          }
        } catch (error) {
          console.error('Error loading CRM folders:', error);
          setCRMList([]);
          setCrmListError('Failed to load CRM folders');
        } finally {
          setCrmLoading(false);
        }
      }


      // Sync picker state with field value
      React.useEffect(() => {
        if (schemaItem.content?.multiple) {
          if (Array.isArray(field.value)) {
            const fieldValueString = JSON.stringify(field.value);
            const pickerStateString = JSON.stringify(pickerState);
            if (fieldValueString !== pickerStateString) {
              setPickerState(field.value);
            }
          }
        } else {
          if (field.value !== undefined && field.value !== pickerState) {
            setPickerState(field.value);
          }
        }
      }, [field.value]);

      const handleCrmToggle = (itemValue) => {
        if (schemaItem.content?.multiple) {
          const currentValues = Array.isArray(pickerState) ? pickerState : [];
          let updatedValues;

          if (currentValues.includes(itemValue)) {
            updatedValues = currentValues.filter(val => val !== itemValue);
          } else {
            updatedValues = [...currentValues, itemValue];
          }

          setPickerState(updatedValues);
          helpers.setValue(updatedValues);
          helpers.setTouched(true);
        } else {
          setPickerState(itemValue);
          helpers.setValue(itemValue);
          helpers.setTouched(true);
          setShowCrmDropdown(false);
        }
      };

      const getCrmDisplayText = () => {
        if (schemaItem.content?.multiple) {
          const selectedCount = Array.isArray(pickerState) ? pickerState.length : 0;
          if (selectedCount === 0) return "Select customer data...";
          return `${selectedCount} item${selectedCount !== 1 ? 's' : ''} selected`;
        } else {
          if (!pickerState) return "Select customer data...";
          const selectedItem = crmList.find(item => item.value === pickerState);
          return selectedItem ? selectedItem.label : pickerState;
        }
      };

      const filteredCrmList = React.useMemo(() => {
        if (!crmSearchText || crmSearchText.trim().length === 0) return crmList;

        const filtered = crmList?.filter?.(item =>
          item?.label?.toLowerCase().includes(crmSearchText.toLowerCase())
        ) || [];
        return filtered;
      }, [crmSearchText, crmList]);

      return (
        <View style={{
          flex: 1,
          marginVertical: 10,
          zIndex: showCrmDropdown ? 10000 : 1,
          elevation: showCrmDropdown ? 10000 : 1
        }}>
          {/* Error display */}
          {hasError && (
            <Text
              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

          {/* CRM Error display */}
          {crmListError && (
            <Text
              style={{ color: 'orange', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {crmListError}
            </Text>
          )}

          {/* Modern CRM Dropdown Container */}
          <View style={{ position: 'relative', zIndex: showCrmDropdown ? 10001 : 1 }}>
            <Pressable
              onPress={async () => {
                if (!showCrmDropdown && crmList.length === 0) {
                  await handleCRMOpen();
                }
                setShowCrmDropdown(!showCrmDropdown);
              }}
              style={{
                backgroundColor: theme ? 'white' : '#1e293b',
                borderWidth: 1,
                borderColor: hasError ? '#ef4444' : (showCrmDropdown ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
                borderRadius: 12,
                paddingHorizontal: 16,
                paddingVertical: 14,
                minHeight: 50,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 3,
              }}
            >
              <Text style={{
                color: (schemaItem.content?.multiple ? 
                  (Array.isArray(pickerState) && pickerState.length > 0) :
                  pickerState
                ) ? (theme ? '#1e293b' : '#f8fafc') : (theme ? '#64748b' : '#94a3b8'),
                fontSize: 16,
                fontWeight: (schemaItem.content?.multiple ? 
                  (Array.isArray(pickerState) && pickerState.length > 0) :
                  pickerState
                ) ? '600' : '400',
                flex: 1
              }}>
                {getCrmDisplayText()}
              </Text>

              <View
                animation={showCrmDropdown ? 'rotate' : undefined}
                duration={200}
              >
                <FontAwesome
                  name={crmLoading ? 'refresh' : (showCrmDropdown ? 'chevron-up' : 'chevron-down')}
                  size={14}
                  color={theme ? '#64748b' : '#94a3b8'}
                />
              </View>
            </Pressable>

            {/* Dropdown Options */}
            {showCrmDropdown && (
              <View
                animation="fadeInDown"
                duration={200}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: theme ? 'white' : '#1e293b',
                  borderWidth: 1,
                  borderColor: theme ? '#e2e8f0' : '#374151',
                  borderRadius: 12,
                  marginTop: 4,
                  maxHeight: 400,
                  zIndex: 10002,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.15,
                  shadowRadius: 12,
                  elevation: 10002,
                }}
              >
                {/* Search Input */}
                <View style={{ padding: 12, borderBottomWidth: 1, borderBottomColor: theme ? '#f1f5f9' : '#334155' }}>
                  <TextInput
                    value={crmSearchText}
                    onChangeText={setCrmSearchText}
                    placeholder="Search customer data..."
                    {...(Platform.OS !== 'web' && { placeholderTextColor: theme ? '#64748b' : '#94a3b8' })}
                    style={{
                      backgroundColor: theme ? '#f8fafc' : '#0f172a',
                      borderWidth: 1,
                      borderColor: theme ? '#e2e8f0' : '#475569',
                      borderRadius: 8,
                      paddingHorizontal: 12,
                      paddingVertical: 8,
                      fontSize: 14,
                      color: theme ? '#1e293b' : '#f8fafc',
                    }}
                  />
                </View>

                <ScrollView
                  style={{ maxHeight: 330 }}
                  showsVerticalScrollIndicator={false}
                  nestedScrollEnabled
                >
                  {crmLoading ? (
                    <View style={{ padding: 16, alignItems: 'center', minHeight: 50 }}>
                      <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                        Loading customer data...
                      </Text>
                    </View>
                  ) : crmListError ? (
                      <View style={{ padding: 16, alignItems: 'center', minHeight: 50 }}>
                        <Text style={{ color: '#ef4444', fontSize: 14 }}>
                        {crmListError}
                      </Text>
                    </View>
                  ) : filteredCrmList.length === 0 ? (
                        <View style={{ padding: 16, alignItems: 'center', minHeight: 50 }}>
                      <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                            {crmSearchText ? 'No matching customer data found' : 'No customer data available'}
                      </Text>
                    </View>
                  ) : (
                    filteredCrmList.map((item, index) => {
                      const isSelected = schemaItem.content?.multiple ?
                        (Array.isArray(pickerState) && pickerState.includes(item.value)) :
                        pickerState === item.value;

                      return (
                        <Pressable
                          key={item.value || index}
                          onPress={() => handleCrmToggle(item.value)}
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            paddingHorizontal: 16,
                            paddingVertical: 12,
                            minHeight: 60,
                            borderBottomWidth: index < filteredCrmList.length - 1 ? 1 : 0,
                            borderBottomColor: theme ? '#f1f5f9' : '#334155',
                            backgroundColor: isSelected ?
                              (theme ? '#eff6ff' : '#1e3a8a') :
                              'transparent',
                          }}
                        >
                          {schemaItem.content?.multiple && (
                            <Checkbox
                              value={isSelected}
                              onValueChange={() => handleCrmToggle(item.value)}
                              style={{
                                marginRight: 12,
                                transform: [{ scale: 1.1 }]
                              }}
                            />
                          )}

                          <View style={{ flex: 1 }}>
                            <Text style={{
                              fontSize: 16,
                              color: theme ? '#1e293b' : '#f8fafc',
                              fontWeight: '500'
                            }}>
                              {item.label || item.name || 'Unnamed Item'}
                            </Text>
                            {item.description && (
                              <Text style={{
                                fontSize: 14,
                                color: theme ? '#64748b' : '#94a3b8',
                                marginTop: 2
                              }}>
                                {item.description}
                              </Text>
                            )}
                          </View>

                          {/* Selected indicator for single select */}
                          {!schemaItem.content?.multiple && isSelected && (
                            <FontAwesome
                              name="check"
                              size={16}
                              color={theme ? '#0369a1' : '#60a5fa'}
                              style={{ marginLeft: 8 }}
                            />
                          )}
                        </Pressable>
                      );
                    })
                  )}
                </ScrollView>
              </View>
            )}

            {/* Backdrop to close dropdown */}
            {showCrmDropdown && (
              <Pressable
                onPress={() => setShowCrmDropdown(false)}
                style={{
                  position: 'absolute',
                  top: -1000,
                  left: -1000,
                  right: -1000,
                  bottom: -1000,
                  zIndex: 10000,
                }}
              />
            )}
          </View>

          {/* Selection summary */}
          {schemaItem.content?.multiple && Array.isArray(pickerState) && pickerState.length > 0 && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#0369a1' : '#60a5fa',
              marginTop: 5,
              fontWeight: '500'
            }}>
              {pickerState.length} customer data item{pickerState.length !== 1 ? 's' : ''} selected
            </Text>
          )}

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}
        </View>
      )
      case 'team':
      return (
        <View style={{ flex: 1, marginVertical: 10, zIndex: 999999, position: 'relative' }}>
          {/* Error display */}
          {hasError && (
            <Text
              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

          {/* Modern User Selection Container */}
          <View style={{
            backgroundColor: theme ? 'white' : '#1e293b',
            borderWidth: 1,
            borderColor: hasError ? '#ef4444' : theme ? '#e2e8f0' : '#374151',
            borderRadius: 12,
            paddingHorizontal: 16,
            paddingVertical: 14,
            minHeight: 50,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 999999,
            zIndex: 999999,
            position: 'relative'
          }}>
            <UserFinder
              onOpen={() => undefined}
              onClose={() => undefined}
              forceSingle={true}
              forceRow
              removePermission
              selected={value}
              setCallback={_helperCallback}
              theme={theme}
              hasError={hasError}
            />
          </View>

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}
        </View>
      )
    case 'user_stores':
      return (
        <View style={{ flex: 1, marginVertical: 10, zIndex: 999999, position: 'relative' }}>
          {/* Error display */}
          {hasError && (
            <Text
              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

          {/* Modern User Selection Container */}
          <View style={{
            backgroundColor: theme ? 'white' : '#1e293b',
            borderWidth: 1,
            borderColor: hasError ? '#ef4444' : theme ? '#e2e8f0' : '#374151',
            borderRadius: 12,
            paddingHorizontal: 16,
            paddingVertical: 14,
            minHeight: 50,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 999999,
            zIndex: 999999,
            position: 'relative'
          }}>
            <UserFinder
              onOpen={() => undefined}
              onClose={() => undefined}
              forceSingle={true}
              forceRow
              removePermission
              selected={value}
              setCallback={_helperCallback}
              theme={theme}
              hasError={hasError}
            />
          </View>

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}
        </View>
      )
    case 'user_select':
      return (
        <View style={{
          flex: 1,
          marginVertical: 10,
          zIndex: 999999,
          position: 'relative'
        }}>
          {/* Error display */}
          {hasError && (
            <Text
              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

          {/* Modern User Selection Container - Matching product_select dropdown style exactly */}
          <View style={{
            backgroundColor: theme ? 'white' : '#1e293b',
            borderWidth: 1,
            borderColor: hasError ? '#ef4444' : (theme ? '#e2e8f0' : '#374151'),
            borderRadius: 12,
            paddingHorizontal: 16,
            paddingVertical: 14,
            minHeight: 50,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 999999,
            zIndex: 999999,
            position: 'relative'
          }}>
            <UserFinder
              onOpen={() => undefined}
              onClose={() => undefined}
              forceSingle={schemaItem.content?.multiple !== true}
              forceRow
              team
              removePermission
              selected={value}
              setCallback={_helperCallback}
              theme={theme}
              hasError={hasError}
            />
          </View>

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}
        </View>
      )
    case 'currency':
      const currencyList = CurrencyExtractor.extract() || [];

      // Add fallback currencies if extraction fails
      const safeCurrencyList = currencyList.length > 0 ? currencyList : [
        { label: 'USD - US Dollar', value: 'USD' },
        { label: 'EUR - Euro', value: 'EUR' },
        { label: 'GBP - British Pound', value: 'GBP' },
        { label: 'JPY - Japanese Yen', value: 'JPY' }
      ];

      return (
        <View style={{ flex: 1, marginBottom: height > MIN_DROPDOWN_HIEGHT ? height - MIN_DROPDOWN_HIEGHT : 0, position: 'relative', zIndex: 1000 }}>
          <View style={{ flex: 1, flexDirection: 'row', gap: 3 }}>
            {hasError && <Text style={{ color: 'red', padding: 2 }}>{meta.error}</Text>}
          </View>
          <View style={{ flexDirection: 'row', flex: 1, gap: 5 }}>
            <DropDownPicker
              items={safeCurrencyList}
              open={pickerOpen}
              onOpen={() => setHeight(250)}
              onClose={() => setHeight(MIN_DROPDOWN_HIEGHT)}
              zIndex={3}
              dropDownContainerStyle={{
                flex: 1,
                borderColor: '#4791bd',
                backgroundColor: theme ? 'white' : '#333333',
                maxHeight: 200,
                overflow: 'visible',
                position: 'absolute',
                zIndex: 1000
              }}
              dropDownDirection="AUTO"
              setOpen={setPickerOpen}
              value={pickerState}
              setValue={(callback) => {
                const newValue = typeof callback === 'function' ? callback(pickerState) : callback;
                setPickerState(newValue);
                helpers.setValue(newValue);
                helpers.setTouched(true);
              }}
              placeholder='Select Currency'
              style={{
                flex: 1,
                backgroundColor: theme ? 'white' : '#333333',
                borderColor: hasError ? 'red' : '#4791bd',
                maxWidth: "50%"
              }}
              textStyle={{
                color: theme ? 'black' : 'white'
              }}
              containerStyle={{ flex: 1, minWidth: 100, maxWidth: "50%" }}
              listMode="SCROLLVIEW"
              scrollViewProps={{
                nestedScrollEnabled: true
              }}
            />
            <InputFieldStyled
              value={localText || field.value || ''}
              ref={inputRef}
              multiline={schemaItem.content?.ref === 'multiline' ? true : false}
              editable={!readOnly}
              onChangeText={(text) => {
                if (!readOnly) {
                  setLocalText(text);
                  helpers.setValue(text);
                  helpers.setTouched(true);
                }
              }}
              enablesReturnKeyAutomatically={true}
              enterKeyHint={index === length - 1 ? 'done' : 'next'}
              inputMode="numeric"
              style={{ flex: 1, color: theme ? 'black' : 'white', maxWidth: "50%", opacity: readOnly ? 0.7 : 1 }}
              onFocus={() => {
                // Set focus time IMMEDIATELY and synchronously before any blur can occur
                if (Platform.OS === 'web') {
                  focusTimeRef.current = getCurrentTime();
                  // Start blur prevention proactively on focus for web touch devices
                  // This prevents validation from running during the keyboard appearance transition
                  if (isTouchDeviceRef.current) {
                    blurPreventionState.startPrevention();
                  }
                }
                if (!readOnly && props.focusCallback) props.focusCallback();
              }}
              onMouseDown={() => {
                // Set focus time even earlier - on mouse down/touch start
                if (Platform.OS === 'web' && isTouchDeviceRef.current) {
                  focusTimeRef.current = getCurrentTime();
                }
              }}
              onTouchStart={() => {
                // Set focus time even earlier - on touch start
                if (Platform.OS === 'web' && isTouchDeviceRef.current) {
                  focusTimeRef.current = getCurrentTime();
                }
              }}
              {...(Platform.OS !== 'web' && {
                onSubmitEditing: () => {
                  if (!readOnly) {
                    field.onBlur(schemaItem.title);
                    helpers.setValue(localText || field.value);
                    helpers.setTouched(true, true);
                    handleKeyPress(index);
                    if (props.blurCallback) props.blurCallback();
                  }
                },
              })}
              onEndEditing={() => {
                if (!readOnly) {
                  helpers.setValue(localText || field.value);
                  helpers.setTouched(true, true);
                  if (props.blurCallback) props.blurCallback()
                }
              }}
              onBlur={(e) => {
                if (!readOnly) {
                  // On web touch devices, detect false blurs but DON'T try to prevent them
                  // Instead, just skip validation to prevent form remounting
                  if (Platform.OS === 'web' && isTouchDeviceRef.current) {
                    const now = getCurrentTime();
                    const timeSinceFocus = now - focusTimeRef.current;
                    // If blur happens within 1000ms of focus, it's likely a false blur from keyboard appearing
                    if (timeSinceFocus < 1000) {
                      // Start blur prevention to block validation and form remounting
                      // But DON'T try to prevent the blur or refocus - that makes it worse
                      blurPreventionState.startPrevention();
                      // Let the blur happen naturally, but skip calling field.onBlur to prevent validation
                      // This prevents form remounting without fighting the browser
                      return; // Don't call field.onBlur, which prevents validation from running
                    }
                  }
                  // Clear blur prevention if it was active
                  blurPreventionState.stopPrevention();
                  // Only call field.onBlur if it's a legitimate blur event
                  field.onBlur(schemaItem.title);
                  if (props.blurCallback) props.blurCallback()
                }
              }}
              placeholder={schemaItem.validation?.placeHolder ? schemaItem.validation.placeHolder : schemaItem.content?.label || (schemaItem.title ? formatTitle(schemaItem.title) : "Amount")}
            />
          </View>
        </View>
      )
    case 'price':
      const priceCurrencyList = CurrencyExtractor.extract() || [];
      const safePriceCurrencyList = priceCurrencyList.length > 0 ? priceCurrencyList : [
        { label: 'USD - US Dollar', value: 'USD' },
        { label: 'EUR - Euro', value: 'EUR' },
        { label: 'GBP - British Pound', value: 'GBP' },
        { label: 'JPY - Japanese Yen', value: 'JPY' }
      ];

      const [priceValue, setPriceValue] = useState(field.value?.amount?.toString() || field.value?.toString() || '');
      const [priceCurrency, setPriceCurrency] = useState(field.value?.currency || pickerState || 'USD');
      const [isPriceFocused, setIsPriceFocused] = useState(false);
      const [showPriceCurrencyDropdown, setShowPriceCurrencyDropdown] = useState(false);

      // Sync with field value changes
      React.useEffect(() => {
        if (field.value !== undefined) {
          if (typeof field.value === 'object' && field.value !== null) {
            setPriceValue(field.value.amount?.toString() || '');
            setPriceCurrency(field.value.currency || 'USD');
          } else {
            setPriceValue(field.value?.toString() || '');
          }
        }
      }, [field.value]);

      const handlePriceChange = (text: string) => {
        // Allow empty string, numbers, and decimal points
        const numericRegex = /^-?\d*\.?\d*$/;
        if (text === '' || numericRegex.test(text)) {
          setPriceValue(text);
          if (text === '') {
            helpers.setValue({ amount: undefined, currency: priceCurrency });
          } else {
            const numericValue = parseFloat(text);
            if (!isNaN(numericValue)) {
              helpers.setValue({ amount: numericValue, currency: priceCurrency });
            }
          }
          helpers.setTouched(true);
        }
      };

      const handlePriceCurrencyChange = (currency: string) => {
        setPriceCurrency(currency);
        const numericValue = priceValue ? parseFloat(priceValue) : undefined;
        const priceObject = {
          amount: (numericValue !== undefined && !isNaN(numericValue)) ? numericValue : undefined,
          currency: currency
        };
        helpers.setValue(priceObject);
        helpers.setTouched(true);
      };

      return (
        <View style={{
          flex: 1,
          marginVertical: 10,
          zIndex: showPriceCurrencyDropdown ? 10000 : 1,
          elevation: showPriceCurrencyDropdown ? 10000 : 1
        }}>
          {/* Error display */}
          {hasError && (
            <Text
              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

          {/* Modern Price Input Container */}
          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
            {/* Currency Selector */}
            {schemaItem.content?.showCurrency !== false && (
              <View style={{ position: 'relative', zIndex: showPriceCurrencyDropdown ? 10001 : 1, minWidth: 120 }}>
                <Pressable
                  onPress={() => setShowPriceCurrencyDropdown(!showPriceCurrencyDropdown)}
                  style={{
                    backgroundColor: theme ? 'white' : '#1e293b',
                    borderWidth: 1,
                    borderColor: hasError ? '#ef4444' : (showPriceCurrencyDropdown ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
                    borderRadius: 12,
                    paddingHorizontal: 16,
                    paddingVertical: 14,
                    minHeight: 50,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.1,
                    shadowRadius: 4,
                    elevation: 3,
                  }}
                >
                  <Text style={{
                    color: theme ? '#1e293b' : '#f8fafc',
                    fontSize: 16,
                    fontWeight: '600',
                    flex: 1
                  }}>
                    {safePriceCurrencyList.find(c => c.value === priceCurrency)?.value || 'USD'}
                  </Text>
                  <FontAwesome
                    name={showPriceCurrencyDropdown ? 'chevron-up' : 'chevron-down'}
                    size={14}
                    color={theme ? '#64748b' : '#94a3b8'}
                  />
                </Pressable>

                {/* Currency Dropdown */}
                {showPriceCurrencyDropdown && (
                  <View
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: theme ? 'white' : '#1e293b',
                      borderWidth: 1,
                      borderColor: theme ? '#e2e8f0' : '#374151',
                      borderRadius: 12,
                      marginTop: 4,
                      maxHeight: 200,
                      zIndex: 10002,
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 8 },
                      shadowOpacity: 0.15,
                      shadowRadius: 12,
                      elevation: 10002,
                    }}
                  >
                    <ScrollView
                      style={{ maxHeight: 200 }}
                      showsVerticalScrollIndicator={false}
                      nestedScrollEnabled
                    >
                      {safePriceCurrencyList.map((currency, index) => (
                        <Pressable
                          key={currency.value || index}
                          onPress={() => {
                            handlePriceCurrencyChange(currency.value);
                            setShowPriceCurrencyDropdown(false);
                          }}
                          style={({ pressed }) => ({
                            paddingHorizontal: 16,
                            paddingVertical: 14,
                            backgroundColor: pressed
                              ? (theme ? '#f1f5f9' : '#334155')
                              : (priceCurrency === currency.value
                                ? (theme ? '#e0f2fe' : '#1e40af20')
                                : 'transparent'),
                            borderBottomWidth: index < safePriceCurrencyList.length - 1 ? 1 : 0,
                            borderBottomColor: theme ? '#f1f5f9' : '#334155',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          })}
                        >
                          <Text style={{
                            color: priceCurrency === currency.value
                              ? (theme ? '#0369a1' : '#60a5fa')
                              : (theme ? '#1e293b' : '#f8fafc'),
                            fontSize: 16,
                            fontWeight: priceCurrency === currency.value ? '600' : '400',
                            flex: 1
                          }}>
                            {currency.label}
                          </Text>
                          {priceCurrency === currency.value && (
                            <FontAwesome
                              name="check"
                              size={14}
                              color={theme ? '#0369a1' : '#60a5fa'}
                            />
                          )}
                        </Pressable>
                      ))}
                    </ScrollView>
                  </View>
                )}
              </View>
            )}

            {/* Price Input */}
            <View style={{ flex: 1 }}>
              <TextInput
                ref={inputRef}
                value={priceValue}
                onChangeText={handlePriceChange}
                onFocus={() => {
                  // Set focus time IMMEDIATELY and synchronously before any blur can occur
                  if (Platform.OS === 'web') {
                    focusTimeRef.current = getCurrentTime();
                  }
                  setIsPriceFocused(true);
                  if (props.focusCallback) props.focusCallback();
                }}
                onMouseDown={() => {
                  // Set focus time even earlier - on mouse down/touch start
                  if (Platform.OS === 'web' && isTouchDeviceRef.current) {
                    focusTimeRef.current = getCurrentTime();
                  }
                }}
                onTouchStart={() => {
                  // Set focus time even earlier - on touch start
                  if (Platform.OS === 'web' && isTouchDeviceRef.current) {
                    focusTimeRef.current = getCurrentTime();
                  }
                }}
                onBlur={(e) => {
                  // On web touch devices, prevent blur if it happens too quickly after focus
                  if (Platform.OS === 'web' && isTouchDeviceRef.current) {
                    const now = getCurrentTime();
                    const timeSinceFocus = now - focusTimeRef.current;
                    // Increased to 1000ms to be more aggressive in preventing false blurs
                    if (timeSinceFocus < 1000) {
                      // Start blur prevention to block validation and form remounting
                      blurPreventionState.startPrevention();
                      // Prevent default blur behavior
                      e?.preventDefault?.();
                      e?.stopPropagation?.();
                      // Refocus with multiple attempts to ensure it works
                      const refocus = () => {
                        if (inputRef.current) {
                          try {
                            inputRef.current.focus();
                          } catch (err) {
                            console.warn('Failed to refocus input:', err);
                          }
                        }
                      };
                      // Try immediately
                      refocus();
                      // Try after short delay
                      setTimeout(refocus, 50);
                      // Try after longer delay as fallback
                      setTimeout(refocus, 150);
                      return; // Don't process this blur event - CRITICAL: Don't call field.onBlur
                    }
                  }
                  // Clear blur prevention if it was active
                  blurPreventionState.stopPrevention();
                  setIsPriceFocused(false);
                  field.onBlur(schemaItem.title);
                  if (props.blurCallback) props.blurCallback();
                }}
                keyboardType="decimal-pad"
                inputMode="decimal"
                placeholder={schemaItem.validation?.placeHolder || schemaItem.content?.label || (schemaItem.title ? formatTitle(schemaItem.title) : "Enter price")}
                style={{
                  backgroundColor: theme ? 'white' : '#1e293b',
                  borderWidth: 1,
                  borderColor: hasError ? '#ef4444' : (isPriceFocused ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
                  borderRadius: 12,
                  paddingHorizontal: 16,
                  paddingVertical: 14,
                  fontSize: 16,
                  color: theme ? '#1e293b' : '#f8fafc',
                  minHeight: 50,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.1,
                  shadowRadius: 4,
                  elevation: 3,
                }}
                {...(Platform.OS !== 'web' && {
                  placeholderTextColor: theme ? '#64748b' : '#94a3b8',
                  returnKeyType: index === length - 1 ? 'done' : 'next',
                  onSubmitEditing: () => handleKeyPress(index),
                })}
              />
            </View>
          </View>

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}

          {/* Backdrop to close currency dropdown */}
          {showPriceCurrencyDropdown && (
            <Pressable
              onPress={() => setShowPriceCurrencyDropdown(false)}
              style={{
                position: 'absolute',
                top: -1000,
                left: -1000,
                right: -1000,
                bottom: -1000,
                zIndex: 10000,
              }}
            />
          )}
        </View>
      );
    case 'number':
      const [numberValue, setNumberValue] = useState(field.value?.toString() || '');
      const [isFocused, setIsFocused] = useState(false);

      // Sync with field value changes
      React.useEffect(() => {
        if (field.value !== undefined && field.value?.toString() !== numberValue) {
          setNumberValue(field.value?.toString() || '');
        }
      }, [field.value]);

      const handleNumberChange = (text: string) => {
        // Allow empty string, numbers, and decimal points
        const numericRegex = /^-?\d*\.?\d*$/;
        if (text === '' || numericRegex.test(text)) {
          setNumberValue(text);
          // Convert to number for form validation, but allow empty string
          const numericValue = text === '' ? '' : parseFloat(text);
          helpers.setValue(isNaN(numericValue) ? '' : numericValue);
          helpers.setTouched(true);
        }
      };

      return (
        <View style={{ flex: 1, marginVertical: 10, ...props.customStyle }}>
          {/* Error display */}
          {hasError && (
            <Text

              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

          {/* Modern Tamagui Number Input */}
          <TextInput
            ref={inputRef}
            value={numberValue}
            onChangeText={handleNumberChange}
            onFocus={() => {
              // Set focus time IMMEDIATELY and synchronously before any blur can occur
              if (Platform.OS === 'web') {
                focusTimeRef.current = getCurrentTime();
                // Start blur prevention proactively on focus for web touch devices
                // This prevents validation from running during the keyboard appearance transition
                if (isTouchDeviceRef.current) {
                  blurPreventionState.startPrevention();
                }
              }
              setIsFocused(true);
              if (props.focusCallback) props.focusCallback();
            }}
            onMouseDown={() => {
              // Set focus time even earlier - on mouse down/touch start
              if (Platform.OS === 'web' && isTouchDeviceRef.current) {
                focusTimeRef.current = getCurrentTime();
              }
            }}
            onTouchStart={() => {
              // Set focus time even earlier - on touch start
              if (Platform.OS === 'web' && isTouchDeviceRef.current) {
                focusTimeRef.current = getCurrentTime();
              }
            }}
            onBlur={(e) => {
              // On web touch devices, detect false blurs but DON'T try to prevent them
              // Instead, just skip validation to prevent form remounting
              if (Platform.OS === 'web' && isTouchDeviceRef.current) {
                const now = getCurrentTime();
                const timeSinceFocus = now - focusTimeRef.current;
                // If blur happens within 1000ms of focus, it's likely a false blur from keyboard appearing
                if (timeSinceFocus < 1000) {
                  // Start blur prevention to block validation and form remounting
                  // But DON'T try to prevent the blur or refocus - that makes it worse
                  blurPreventionState.startPrevention();
                  // Let the blur happen naturally, but skip calling field.onBlur to prevent validation
                  // This prevents form remounting without fighting the browser
                  return; // Don't call field.onBlur, which prevents validation from running
                }
              }
              // Clear blur prevention if it was active
              blurPreventionState.stopPrevention();
              setIsFocused(false);
              field.onBlur(schemaItem.title);
              if (props.blurCallback) props.blurCallback();
            }}
            keyboardType="numeric"
            inputMode="numeric"
            multiline={schemaItem.content?.ref === 'multiline'}
            placeholder={schemaItem.validation?.placeHolder || schemaItem.content?.label || (schemaItem.title ? formatTitle(schemaItem.title) : "Enter number")}
            style={{
              backgroundColor: theme ? 'white' : '#1e293b',
              borderWidth: 1,
              borderColor: hasError ? '#ef4444' : (isFocused ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
              borderRadius: 12,
              paddingHorizontal: 16,
              paddingVertical: 14,
              fontSize: 16,
              color: theme ? '#1e293b' : '#f8fafc',
              minHeight: schemaItem.content?.ref === 'multiline' ? 100 : 50,
              textAlignVertical: schemaItem.content?.ref === 'multiline' ? 'top' : 'center',
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
            }}
            {...(Platform.OS !== 'web' && { placeholderTextColor: theme ? '#64748b' : '#94a3b8' })}
          />

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}

          {/* Validation info */}
          {(schemaItem.validation?.minValue !== undefined || schemaItem.validation?.maxValue !== undefined) && (
            <Text style={{
              fontSize: 11,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 3
            }}>
              {schemaItem.validation?.minValue !== undefined && schemaItem.validation?.maxValue !== undefined
                ? `Range: ${schemaItem.validation.minValue} - ${schemaItem.validation.maxValue}`
                : schemaItem.validation?.minValue !== undefined
                  ? `Minimum: ${schemaItem.validation.minValue}`
                  : `Maximum: ${schemaItem.validation.maxValue}`
              }
            </Text>
          )}
        </View>
      );
    case 'dropDown':
    case 'dropdown':
      const enumArray = schemaItem.content?.enum || schemaItem.validation?.enum || schemaItem.content?.validation?.enum || [];

      // Handle fallback for empty enumArray
      if (!enumArray || enumArray.length === 0) {
        return (
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row' }}>
              <Text style={{ color: 'orange', padding: 2 }}>
                No options available for this dropdown
              </Text>
            </View>
          </View>
        );
      }

      const newItemArray = [];
      try {
        if (enumArray.length > 0 && typeof enumArray[0] !== 'object') {
          enumArray.forEach(item => {
            if (item !== null && item !== undefined) {
              newItemArray.push({
                label: toTitleCase(String(item)) || String(item),
                value: item
              });
            }
          });
        } else if (enumArray && Array.isArray(enumArray)) {
          // Handle object arrays with fallback
          enumArray.forEach(item => {
            if (item && typeof item === 'object') {
              newItemArray.push({
                label: item.label || item.name || String(item.value) || 'Unknown',
                value: item.value || item.id || item
              });
            } else if (item !== null && item !== undefined) {
              newItemArray.push({
                label: String(item),
                value: item
              });
            }
          });
        }
      } catch (error) {
        console.error('Error processing dropdown items:', error);
        return (
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row' }}>
              <Text style={{ color: 'red', padding: 2 }}>
                Error loading dropdown options
              </Text>
            </View>
          </View>
        );
      }

      // Fallback if no valid items after processing
      if (newItemArray.length === 0) {
        return (
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row' }}>
              <Text style={{ color: 'orange', padding: 2 }}>
                No valid options found
              </Text>
            </View>
          </View>
        );
      }

      const [showDropdown, setShowDropdown] = useState(false);

      // Sync picker state with field value
      // Only update if the value actually changed and doesn't match pickerState
      React.useEffect(() => {
        if (field.value !== undefined && field.value !== null && field.value !== pickerState) {
          // Check if it's a different value (not just reference equality)
          const valueToSet = typeof field.value === 'object' && field.value !== null
            ? (field.value._id || field.value.id || field.value.value || field.value)
            : field.value;

          if (valueToSet !== pickerState) {
            setPickerState(valueToSet);
          }
        }
      }, [field.value]);

      const selectedItem = newItemArray.find(item => item.value === pickerState);

      return (
        <View style={{
          flex: 1,
          marginVertical: 10,
          zIndex: showDropdown ? 10000 : 1,
          elevation: showDropdown ? 10000 : 1
        }}>
          {/* Error display */}
          {hasError && (
            <Text

              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

        

          {/* Modern Tamagui Dropdown */}
          <View style={{ position: 'relative', zIndex: showDropdown ? 10001 : 1 }}>
            <Pressable
              onPress={() => setShowDropdown(!showDropdown)}
              style={{
                backgroundColor: theme ? 'white' : '#1e293b',
                borderWidth: 1,
                borderColor: hasError ? '#ef4444' : (showDropdown ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
                borderRadius: 12,
                paddingHorizontal: 16,
                paddingVertical: 14,
                minHeight: 50,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 3,
              }}
            >
              <Text style={{
                color: selectedItem ? (theme ? '#1e293b' : '#f8fafc') : (theme ? '#64748b' : '#94a3b8'),
                fontSize: 16,
                fontWeight: selectedItem ? '600' : '400',
                flex: 1
              }}>
                {selectedItem ? selectedItem.label : "Select an option..."}
              </Text>

              <View
                animation={showDropdown ? 'rotate' : undefined}
                duration={200}
              >
                <FontAwesome
                  name={showDropdown ? 'chevron-up' : 'chevron-down'}
                  size={14}
                  color={theme ? '#64748b' : '#94a3b8'}
                />
              </View>
            </Pressable>

            {/* Dropdown Options */}
            {showDropdown && (
              <View
                animation="fadeInDown"
                duration={200}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: theme ? 'white' : '#1e293b',
                  borderWidth: 1,
                  borderColor: theme ? '#e2e8f0' : '#374151',
                  borderRadius: 12,
                  marginTop: 4,
                  maxHeight: 300,
                  zIndex: 10002,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.15,
                  shadowRadius: 12,
                  elevation: 10002,
                }}
              >
                <ScrollView
                  style={{ maxHeight: 300 }}
                  showsVerticalScrollIndicator={false}
                  nestedScrollEnabled
                >
                  {newItemArray.map((item, index) => (
                    <Pressable
                      key={item.value || index}
                      onPress={() => {
                        // Close dropdown first for better UX
                        setShowDropdown(false);
                        // Update picker state for immediate UI feedback
                        setPickerState(item.value);
                        // Clear any existing error immediately
                        helpers.setError(undefined);
                        // Set the form value synchronously to trigger immediate validation
                        helpers.setValue(item.value);
                        // Mark as touched to ensure validation runs
                        helpers.setTouched(true);
                      }}
                      style={({ pressed }) => ({
                        paddingHorizontal: 16,
                        paddingVertical: 14,
                        backgroundColor: pressed
                          ? (theme ? '#f1f5f9' : '#334155')
                          : (pickerState === item.value
                            ? (theme ? '#e0f2fe' : '#1e40af20')
                            : 'transparent'),
                        borderBottomWidth: index < newItemArray.length - 1 ? 1 : 0,
                        borderBottomColor: theme ? '#f1f5f9' : '#334155',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      })}
                    >
                      <Text style={{
                        color: pickerState === item.value
                          ? (theme ? '#0369a1' : '#60a5fa')
                          : (theme ? '#1e293b' : '#f8fafc'),
                        fontSize: 16,
                        fontWeight: pickerState === item.value ? '600' : '400',
                        flex: 1
                      }}>
                        {item.label}
                      </Text>

                      {pickerState === item.value && (
                        <FontAwesome
                          name="check"
                          size={14}
                          color={theme ? '#0369a1' : '#60a5fa'}
                        />
                      )}
                    </Pressable>
                  ))}
                </ScrollView>
              </View>
            )}

            {/* Backdrop to close dropdown */}
            {showDropdown && (
              <Pressable
                onPress={() => setShowDropdown(false)}
                style={{
                  position: 'absolute',
                  top: -1000,
                  left: -1000,
                  right: -1000,
                  bottom: -1000,
                  zIndex: 10000,
                }}
              />
            )}
          </View>

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}
        </View>
      );
    case 'boolean':
      useEffect(() => {
        if (!field.value) {
          helpers.setValue(switchState);
        }
      }, [])

      const getLabel = () =>{ 
        if(schemaItem?.content?.labels) {
            return switchState ? schemaItem.content.labels[0] : schemaItem.content.labels[1];
        } else { 
          return switchState ? "True" : "False";
        }
      }

      return (
        <View style={{ flex: 1, marginVertical: 10, zIndex: 999999, position: 'relative' }}>
          {/* Error display */}
          {hasError && (
            <Text

              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

 

          {/* Modern Switch Container */}
          <Pressable
            onPress={() => handleSwitchChange(!switchState)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: theme ? 'white' : '#1e293b',
              borderWidth: 1,
              borderColor: theme ? '#e2e8f0' : '#374151',
              borderRadius: 12,
              paddingHorizontal: 16,
              paddingVertical: 14,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
            }}
          >
            <Switch
              checked={switchState}
              onCheckedChange={handleSwitchChange}
              size="$4"
              style={{ marginRight: 12 }}
            >
              <Switch.Thumb backgroundColor={theme ? "white" : "#64748b"} />
            </Switch>
            <Text style={{
              fontSize: 16,
              color: theme ? '#1e293b' : '#f8fafc',
              fontWeight: '500',
              flex: 1
            }}>
              {getLabel()}
            </Text>
          </Pressable>

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}
        </View>
      );
    case 'website':
    case 'url':
      const [urlValue, setUrlValue] = useState(field.value?.toString() || '');
      const [isUrlFocused, setIsUrlFocused] = useState(false);

      // Sync with field value changes
      React.useEffect(() => {
        if (field.value !== undefined && field.value?.toString() !== urlValue) {
          setUrlValue(field.value?.toString() || '');
        }
      }, [field.value]);

      const handleUrlChange = (text: string) => {
        setUrlValue(text);
        helpers.setValue(text);
        helpers.setTouched(true);
      };

      // URL validation
      const isValidUrl = (url: string) => {
        try {
          new URL(url);
          return true;
        } catch {
          return false;
        }
      };

      const urlHasError = hasError || (urlValue && !isValidUrl(urlValue));

      return (
        <View style={{ flex: 1, marginVertical: 10, zIndex: 999999, position: 'relative' }}>
          {/* Error display */}
          {urlHasError && (
            <Text

              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error || 'Please enter a valid URL'}
            </Text>
          )}



          {/* Modern Tamagui URL Input */}
          <TextInput
            ref={inputRef}
            value={urlValue}
            onChangeText={handleUrlChange}
            onFocus={() => {
              // Set focus time IMMEDIATELY and synchronously before any blur can occur
              if (Platform.OS === 'web') {
                focusTimeRef.current = getCurrentTime();
              }
              setIsUrlFocused(true);
              if (props.focusCallback) props.focusCallback();
            }}
            onMouseDown={() => {
              // Set focus time even earlier - on mouse down/touch start
              if (Platform.OS === 'web' && isTouchDeviceRef.current) {
                focusTimeRef.current = getCurrentTime();
              }
            }}
            onTouchStart={() => {
              // Set focus time even earlier - on touch start
              if (Platform.OS === 'web' && isTouchDeviceRef.current) {
                focusTimeRef.current = getCurrentTime();
              }
            }}
            onBlur={(e) => {
              // On web touch devices, prevent blur if it happens too quickly after focus
              if (Platform.OS === 'web' && isTouchDeviceRef.current) {
                const now = getCurrentTime();
                const timeSinceFocus = now - focusTimeRef.current;
                // Increased to 1000ms to be more aggressive in preventing false blurs
                if (timeSinceFocus < 1000) {
                  // Start blur prevention to block validation and form remounting
                  blurPreventionState.startPrevention();
                  // Prevent default blur behavior
                  e?.preventDefault?.();
                  e?.stopPropagation?.();
                  // Refocus with multiple attempts to ensure it works
                  const refocus = () => {
                    if (inputRef.current) {
                      try {
                        inputRef.current.focus();
                      } catch (err) {
                        console.warn('Failed to refocus input:', err);
                      }
                    }
                  };
                  // Try immediately
                  refocus();
                  // Try after short delay
                  setTimeout(refocus, 50);
                  // Try after longer delay as fallback
                  setTimeout(refocus, 150);
                  return; // Don't process this blur event - CRITICAL: Don't call field.onBlur
                }
              }
              // Clear blur prevention if it was active
              blurPreventionState.stopPrevention();
              setIsUrlFocused(false);
              field.onBlur(schemaItem.title);
              if (props.blurCallback) props.blurCallback();
            }}
            keyboardType="url"
            autoCapitalize="none"
            autoCorrect={false}
            placeholder={schemaItem.validation?.placeHolder || "https://example.com"}
            style={{
              backgroundColor: theme ? 'white' : '#1e293b',
              borderWidth: 1,
              borderColor: urlHasError ? '#ef4444' : (isUrlFocused ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
              borderRadius: 12,
              paddingHorizontal: 16,
              paddingVertical: 14,
              fontSize: 16,
              color: theme ? '#1e293b' : '#f8fafc',
              minHeight: 50,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
            }}
            {...(Platform.OS !== 'web' && { placeholderTextColor: theme ? '#64748b' : '#94a3b8' })}
          />

          {/* Help text */}
          <Text style={{
            fontSize: 12,
            color: theme ? '#64748b' : '#94a3b8',
            marginTop: 5,
            fontStyle: 'italic'
          }}>
            Enter a valid URL (e.g., https://example.com)
          </Text>
        </View>
      );
    case 'Mobile':
    case 'Phone':
    case 'mobile':
    case 'phone':
      const [phoneValue, setPhoneValue] = useState(field.value?.toString() || '');
      const [isPhoneFocused, setIsPhoneFocused] = useState(false);

      // Phone validation - basic check for minimum length
      const minPhoneLength = 10;
      const isValidPhone = phoneValue.length === 0 || phoneValue.replace(/\D/g, '').length >= minPhoneLength;

      // Sync with field value changes
      React.useEffect(() => {
        if (field.value !== undefined && field.value?.toString() !== phoneValue) {
          setPhoneValue(field.value?.toString() || '');
        }
      }, [field.value]);

      const handlePhoneChange = (value: string) => {
        setPhoneValue(value);
        helpers.setValue(value);
        helpers.setTouched(true);

        // Real-time phone validation
        const digitsOnly = value.replace(/\D/g, '');
        if (value.length > 0 && digitsOnly.length < minPhoneLength) {
          helpers.setError(`Phone number must be at least ${minPhoneLength} digits`);
        } else {
          helpers.setError(null);
        }
      };

      const phoneHasError = hasError || !isValidPhone;

      return (
        <View style={{
          flex: 1,
          marginVertical: 10,
          marginBottom: isPhoneFocused ? 220 : 10, // Add space for dropdown when focused
          zIndex: isPhoneFocused ? 1000 : 1,
          elevation: isPhoneFocused ? 1000 : 1,
          ...props.customStyle
        }}>
          {/* Error display */}
          {phoneHasError && (
            <Text style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}>
              {meta.error || `Phone number must be at least ${minPhoneLength} digits`}
            </Text>
          )}

          {/* Clean Phone Input */}
          <View
            style={{
              backgroundColor: theme ? 'white' : '#1e293b',
              borderWidth: 1,
              borderColor: phoneHasError ? '#ef4444' : (isPhoneFocused ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
              borderRadius: 12,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
              overflow: 'visible', // Changed to visible to allow dropdown
              position: 'relative',
              zIndex: isPhoneFocused ? 1001 : 1,
              minHeight: 50,
            }}
            {...(Platform.OS === 'web' ? {
              // @ts-ignore - web only prop
              className: 'phone-input-wrapper'
            } : {})}
          >
            <PhoneInput
              inputProps={{
                ref: inputRef,
                style: {
                  backgroundColor: 'transparent',
                  color: theme ? '#1e293b' : '#f8fafc',
                  fontSize: 16,
                  paddingHorizontal: 16,
                  paddingVertical: 14,
                  minHeight: 50,
                  borderWidth: 0,
                  flex: 1,
                  width: '100%',
                },
                placeholder: schemaItem.content?.label || "Phone Number",
                placeholderTextColor: theme ? '#64748b' : '#94a3b8',
                onFocus: () => {
                  // setIsPhoneFocused(true);
                  if (props.focusCallback) props.focusCallback();
                },
                onBlur: () => {
                  // setIsPhoneFocused(false);
                  field.onBlur(schemaItem.title);
                  if (props.blurCallback) props.blurCallback();
                },
                ...(Platform.OS !== 'web' && {
                  returnKeyType: index === length - 1 ? 'done' : 'next',
                  onSubmitEditing: () => handleKeyPress(index),
                }),
              }}
              country={props.schemaItem.validation?.default || 'us'}
              dropdownStyle={{
                backgroundColor: theme ? 'white' : '#1e293b',
                borderColor: theme ? '#e2e8f0' : '#374151',
                borderWidth: 1,
                borderRadius: 8,
                zIndex: 1002,
                position: "absolute",
                top: 55, // Position below the input
                left: 0,
                width: Platform.OS === 'web' ? '400px' : '100%', // Wider dropdown on web
                minWidth: Platform.OS === 'web' ? 400 : '100%',
                maxHeight: 200,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.15,
                shadowRadius: 8,
                elevation: 1002,
                ...(Platform.OS === 'web' ? { userSelect: 'none' } : {}),
              }}
              containerStyle={{
                position: "relative",
                zIndex: isPhoneFocused ? 1001 : 1,
                backgroundColor: 'transparent',
                borderWidth: 0,
                flex: 1,
                width: '100%',
                overflow: 'visible', // Keep visible for dropdown to show
                flexDirection: 'row',
                alignItems: 'center',
              }}
              buttonStyle={{
                backgroundColor: 'transparent',
                borderWidth: 0,
                paddingHorizontal: 8,
                paddingVertical: 0,
                height: 50,
                justifyContent: 'center',
                alignItems: 'center',
                width: 70, // Increased width to accommodate country code + flag
                maxWidth: 80, // Max width to prevent overflow
                position: 'absolute',
                left: 0,
                zIndex: 10, // Higher z-index so button is clickable
              }}
              buttonTextStyle={{
                color: theme ? '#1e293b' : '#f8fafc',
                fontSize: 14,
                fontWeight: '500',
              }}
              inputStyle={{
                backgroundColor: 'transparent',
                borderWidth: 0,
                color: theme ? '#1e293b' : '#f8fafc',
                fontSize: 16,
                paddingHorizontal: 16,
                paddingVertical: 14,
                minHeight: 50,
                paddingLeft: 60, // Reduced padding - button is clickable with higher z-index
                marginLeft: 0, // Ensure no margin conflicts
                width: '100%',
                flex: 1,
                position: 'relative',
                zIndex: 1, // Lower z-index - button needs to be clickable, but input padding prevents visual overlap
              }}
              onChange={handlePhoneChange}
              value={phoneValue}
            />
          </View>

          {/* Phone validation feedback */}
          {phoneValue.length > 0 && (
            <View style={{ marginTop: 6, flexDirection: 'row', alignItems: 'center', zIndex: 1 }}>
              <Entypo
                name={isValidPhone ? 'check' : 'warning'}
                size={12}
                color={isValidPhone ? '#10b981' : '#f59e0b'}
                style={{ marginRight: 6 }}
              />
              <Text style={{
                fontSize: 12,
                color: isValidPhone ? '#10b981' : '#f59e0b',
                fontWeight: '500'
              }}>
                {isValidPhone ? 'Valid phone number' : `Phone number needs at least ${minPhoneLength} digits`}
              </Text>
            </View>
          )}

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 8,
              fontStyle: 'italic',
              zIndex: 1
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}
        </View>
      )
    case 'checkbox':
      return (
        <View style={{ flex: 1, marginVertical: 10, zIndex: 999999, position: 'relative' }}>
          {/* Error display */}
          {hasError && (
            <Text
              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

          {/* Modern Checkbox Container */}
          <Pressable
            onPress={() => handleSwitchChange(!switchState)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: theme ? 'white' : '#1e293b',
              borderWidth: 1,
              borderColor: theme ? '#e2e8f0' : '#374151',
              borderRadius: 12,
              paddingHorizontal: 16,
              paddingVertical: 14,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
            }}
          >
            <Checkbox
              value={switchState}
              onValueChange={handleSwitchChange}
              style={{
                marginRight: 12,
                transform: [{ scale: 1.2 }]
              }}
            />
            <Text style={{
              fontSize: 16,
              color: theme ? '#1e293b' : '#f8fafc',
              fontWeight: '500',
              flex: 1
            }}>
              {schemaItem.content?.label || (schemaItem.title ? formatTitle(schemaItem.title) : "Check this option")}
            </Text>
          </Pressable>

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}
        </View>
      );
    case 'tags':
      const tagsLabel = schemaItem.content?.label || (schemaItem.title ? formatTitle(schemaItem.title) : "Tags");

      return (
        <View style={{ flex: 1, marginVertical: 10, zIndex: 999999, position: 'relative', width: '100%' }}>
          {/* Error display */}
          {hasError && (
            <Text
              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

          {/* Label - matching other fields */}
          <Text style={{
            fontSize: 14,
            fontWeight: '500',
            color: theme ? '#1e293b' : '#f8fafc',
            marginBottom: 8
          }}>
            {tagsLabel}
          </Text>

          {/* Modern Tag Input with consistent styling */}
          <View style={{
            backgroundColor: theme ? 'white' : '#1e293b',
            borderWidth: 1,
            borderColor: hasError ? '#ef4444' : theme ? '#e2e8f0' : '#374151',
            borderRadius: 12,
            paddingHorizontal: 16,
            paddingVertical: 14,
            minHeight: 50,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 999999,
            zIndex: 999999,
            position: 'relative',
            width: '100%',
            alignSelf: 'stretch'
          }}>
            <TagInput
              label={tagsLabel}
              placeHolder={schemaItem.validation?.placeHolder || 'Add tags, separated by space'}
              tags={tags}
              setTags={handleUpdateTags}
              enabled={true}
            />
          </View>

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}
        </View>
      );
    case 'barcode':
      return (
        <View style={{ flexDirection: 'row' }}>
          <Modal isVisible={barcode} onBackdropPress={() => setBarcode(false)} deviceHeight={height} deviceWidth={width} style={styles.modalContainer}>

          </Modal>
          <Pressable style={{ flex: 1 }} onPress={() => setBarcode(true)}>
            <Entypo name="camera" size={24} color="black" />
          </Pressable>
          <InputFieldStyled style={{ flex: 5 }} enterKeyHint='next' onBlur={onBlur(schemaItem.title)} multiline={schemaItem.content?.ref === 'multiline' ? true : false} onChange={helpers.setValue(schemaItem.title)} onChangeText={formRef.current?.onChange(schemaItem.title)} placeholder={schemaItem.content?.label || (schemaItem.title ? formatTitle(schemaItem.title) : "")} />
        </View>
      );
    case 'city':
    case 'address':
    case 'location':
      return (
        <GenerateGetCity ref={inputRef} index={index} length={length} nextFieldFunction={nextFieldFunction as (index: number, length: number) => void} helpers={helpers} field={field} meta={meta} theme={theme} hasError={hasError as boolean | undefined} schemaItem={schemaItem} updateState={(address) => { setCity(address); helpers.setValue(address) }} updateFunction={handleUpdateCity} initialValue={''} />
      );
    case 'image':
      return (
        <View style={{ flex: 1, gap: 10, marginVertical: 20 }}>
          <View style={{ flexDirection: 'row', gap: 5 }}>
            {hasError && <Text style={{ color: 'red', padding: 2 }}>{meta.error}</Text>}
          </View>
          {selectedImage && <>
            <Image source={{ uri: selectedImage }} style={{ borderRadius: 5, minWidth: '100%', resizeMode: 'center', aspectRatio: 1, alignSelf: 'center', padding: 20 }} />
          </>}
          <ImagePickerComponent selected={selectedImage ? true : false} onImageSelected={async (img) => await handleImageSelected(img, '')} />
        </View>
      );
    case 'images':
      return (
        <View style={{ flex: 1, top: -40, gap: 10, marginVertical: 20, minHeight: 40 }}>
          <View style={{ gap: 5 }}>
            <SizableText style={fieldStyles.typeStyle}>Multiple images can be selected</SizableText>
            {hasError && <Text style={{ color: 'red', padding: 2 }}>{meta.error}</Text>}
          </View>
          {selectedImages.length > 0 && <><HorizontalCarousel
            images={selectedImages || null}
            height={200}
          /></>}
          <ImagePickerComponent allowMultiple onImageSelected={async (img) => await handleImagesSelected(img)} />
        </View>
      )
    case 'dateRange':
    case 'daterange':
      const [showSelector, setShowSelector] = useState(false);

      const selectionRange = {
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
      }

      return (
        <View style={{ maxHeight: 80, top: 40, zIndex: 100000, height: open ? 800 : undefined, marginBottom: 20 }}>
          {
            showSelector &&
            <Modal
              isVisible={showSelector}
              style={{ position: "absolute", minHeight: 500, minWidth: 700 }}
            >
                <DateRangePicker
                  style={{ flex: 1, transform: [{ scale: 1.2 }] }}
                  ranges={[selectionRange]}
                  onChange={(ranges) => {
                    const { selection } = ranges;
                    setDateSelected(selection);
                    helpers.setValue({ startDate: selection.startDate, endDate: selection.endDate });
                    setShowSelector(null);
                  }}
              />
            </Modal>
          }
          {
            value ?
              <Pressable onPress={() => setShowSelector(true)} style={{ minHeight: 40 }} >
                {
                  dateSelected.startDate === dateSelected.endDate ?
                    <Text style={{ textAlign: "center", marginVertical: 20, borderWidth: 0.2, borderRadius: 10, flex: 1 }}>{moment(dateSelected.startDate).format(globalDate)}</Text>
                    :
                    <View style={{ flexDirection: "row", padding: 4, borderRadius: 20, borderWidth: 1, borderColor: "rgb(90,192,234)" }}>
                      <Button textStyle={{ color: theme ? "black" : "white", fontSize: 12 }} textColor={theme ? "black" : "white"} customStyle={{ borderWidth: 1, minHeight: 40 }} defaultColor={theme ? "white" : "black"} buttonText={moment(dateSelected?.startDate).format(globalDate).toString()} onPress={() => null} />
                      <Button textStyle={{ color: theme ? "black" : "white", fontSize: 12 }} textColor={theme ? "black" : "white"} customStyle={{ borderWidth: 1 }} defaultColor={theme ? "white" : "black"} buttonText={moment(dateSelected?.endDate).format(globalDate).toString()} onPress={() => null} />
                    </View>

                }
              </Pressable>
              :
              <Button buttonText={"Select Date Range"} customStyle={{ minHeight: 40, flex: 1, borderRadius: 5, minWidth: "100%" }} onPress={() => setShowSelector(true)} />
          }
        </View>
      );
    case 'date':
      return (
        <View style={{ flex: 1, marginVertical: 10, zIndex: 999999, position: 'relative' }}>
          {/* Error display */}
          {hasError && (
            <Text
              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

          {/* Modern Date Picker Container */}
          {Platform.OS === 'web' ? (
            <View style={{
              backgroundColor: theme ? 'white' : '#1e293b',
              borderWidth: 1,
              borderColor: hasError ? '#ef4444' : theme ? '#e2e8f0' : '#374151',
              borderRadius: 12,
              paddingHorizontal: 16,
              paddingVertical: 14,
              minHeight: 50,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 999999,
              zIndex: 999999,
              position: 'relative'
            }}>
              {dateSelected ? (
                <Pressable
                  onPress={() => {
                    setDateSelected(null);
                    helpers.setValue(null);
                    helpers.setTouched(true);
                  }}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                    <Entypo
                      name="calendar"
                      size={18}
                      color={theme ? '#64748b' : '#94a3b8'}
                      style={{ marginRight: 12 }}
                    />
                    <Text style={{
                      color: theme ? '#1e293b' : '#f8fafc',
                      fontSize: 16,
                      fontWeight: '600',
                      flex: 1
                    }}>
                      {moment(dateSelected).format(globalDate || 'DD/MM/YYYY')}
                    </Text>
                  </View>
                  <Entypo
                    name="cross"
                    size={16}
                    color={theme ? '#64748b' : '#94a3b8'}
                  />
                </Pressable>
              ) : (
                <Pressable
                  onPress={() => setOpen(true)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                    <Entypo
                      name="calendar"
                      size={18}
                      color={theme ? '#64748b' : '#94a3b8'}
                      style={{ marginRight: 12 }}
                    />
                    <Text style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 16,
                      fontWeight: '400',
                      flex: 1
                    }}>
                      {schemaItem.content?.label || "Select a date..."}
                    </Text>
                  </View>
                  <FontAwesome
                    name="chevron-down"
                    size={14}
                    color={theme ? '#64748b' : '#94a3b8'}
                  />
                </Pressable>
              )}
            </View>
          ) : (
            <View style={{
              backgroundColor: theme ? 'white' : '#1e293b',
              borderWidth: 1,
              borderColor: hasError ? '#ef4444' : theme ? '#e2e8f0' : '#374151',
              borderRadius: 12,
              paddingHorizontal: 16,
              paddingVertical: 14,
              minHeight: 50,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 999999,
              zIndex: 999999,
              position: 'relative'
            }}>
              {dateSelected ? (
                <Pressable
                  onPress={() => {
                    setDateSelected(null);
                    helpers.setValue(null);
                    helpers.setTouched(true);
                  }}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                    <Entypo
                      name="calendar"
                      size={18}
                      color={theme ? '#64748b' : '#94a3b8'}
                      style={{ marginRight: 12 }}
                    />
                    <Text style={{
                      color: theme ? '#1e293b' : '#f8fafc',
                      fontSize: 16,
                      fontWeight: '600',
                      flex: 1
                    }}>
                      {moment(dateSelected).format(globalDate || 'DD/MM/YYYY')}
                    </Text>
                  </View>
                    <Entypo
                      name="cross"
                      size={16}
                      color={theme ? '#64748b' : '#94a3b8'}
                    />
                  </Pressable>
                ) : (
                  <Pressable
                    onPress={() => setDateModal(true)}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                      <Entypo
                        name="calendar"
                        size={18}
                        color={theme ? '#64748b' : '#94a3b8'}
                        style={{ marginRight: 12 }}
                      />
                      <Text style={{
                        color: theme ? '#64748b' : '#94a3b8',
                        fontSize: 16,
                        fontWeight: '400',
                        flex: 1
                      }}>
                        {schemaItem.content?.label || "Select a date..."}
                      </Text>
                      </View>
                    <FontAwesome
                      name="chevron-down"
                      size={14}
                      color={theme ? '#64748b' : '#94a3b8'}
                    />
                  </Pressable>
                )}
              </View>
          )}

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}
        </View>
      );
    case 'timeZone':
    case 'timezone':
      const timeZoneOptions = Intl.supportedValuesOf('timeZone').map((timeZone) => ({
        label: formatTitle(timeZone),
        value: timeZone
      }));

      return (
        <View style={{ flex: 1, marginVertical: 10, zIndex: 999999, position: 'relative' }}>
          <View style={{ flexDirection: 'row', gap: 3 }}>
            {hasError && <Text style={{ color: 'red', padding: 2 }}>{meta.error}</Text>}
          </View>
          <FormControl fullWidth variant="filled">
            <InputLabel id="timezone-label">Select Time Zone</InputLabel>
            <Select
              labelId="timezone-label"
              value={field.value || Intl.DateTimeFormat().resolvedOptions().timeZone || pickerState || ''}
              onChange={(event) => {
                const selectedValue = event.target.value;
                setPickerState(selectedValue);
                helpers.setValue(selectedValue);
                helpers.setTouched(true);
              }}
              displayEmpty
              sx={{
                backgroundColor: theme ? 'white' : '#333333',
                color: theme ? 'black' : 'white',
                '& .MuiFilledInput-root': {
                  backgroundColor: theme ? 'white' : '#333333',
                  '&:hover': {
                    backgroundColor: theme ? 'white' : '#333333',
                  },
                  '&.Mui-focused': {
                    backgroundColor: theme ? 'white' : '#333333',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: theme ? '#666' : '#ccc',
                },
                '& .MuiSelect-select': {
                  color: theme ? 'black' : 'white',
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: theme ? 'white' : '#333333',
                    maxHeight: 300,
                    '& .MuiMenuItem-root': {
                      color: theme ? 'black' : 'white',
                      '&:hover': {
                        backgroundColor: theme ? '#f5f5f5' : '#555',
                      },
                    },
                  },
                },
                container: Platform.OS === 'web' ? document.body : undefined,
              }}
            >
              {timeZoneOptions.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </View>
      )
    case 'color':
      const [color, setColor] = useState('#000000');
      return (
        <View style={{ flex: 1, marginVertical: 20 }}>
          <View style={{ flexDirection: 'row', gap: 3 }}>
            {hasError && <Text style={{ color: 'red', padding: 2 }}>{meta.error}</Text>}
          </View>
          <View style={{ flex: 1 }}>
            <ColorPicker
              ref={inputRef}
              color={color}
              onColorChange={(color) => { setColor(color); helpers.setValue(color) }}
              style={{ flex: 1, borderRadius: 2, alignSelf: "stretch", minWidth: "100%", minHeight: 30, marginVertical: 10 }}
            />
          </View>
        </View>
      )
    case 'dateTime':
    case 'datetime':
      return (
        <View style={{ flex: 1, marginVertical: 20 }}>
          <View style={{ flexDirection: 'row', gap: 3 }}>
            {hasError && <Text style={{ color: 'red', padding: 2 }}>{meta.error}</Text>}
          </View>
          <View style={{ flex: 1 }}>
            {
              Platform.OS === 'web' ?
                <View style={{ flex: 1, flexDirection: "row", flexWrap: "wrap" }}>
                  {/* <DatePickerModal
                    locale="en"
                    mode="single"
                    visible={open}
                    onDismiss={()=> {onDismissSingle(); setTimeOpen(true); }}
                    date={moment().toDate()}
                    calendarIcon="calendar"
                    onConfirm={({ date }) => onConfirmSingle(date)}
                  />
                  <TimePickerModal
                    locale="en"
                    visible={time}
                    onDismiss={() => setTimeOpen(false)}
                    onConfirm={value => { handleTimeChange(value); setTimeOpen(false) }} /> */}
                  {
                    dateSelected ?
                      <View style={{ flex: 3, flexDirection: 'row', justifyContent: 'space-between', backgroundColor: '#ececec', paddingHorizontal: 10, marginVertical: 5, borderRadius: 20, borderTopEndRadius: 0, borderBottomEndRadius: 0, padding: 5 }}>
                        <Text adjustsFontSizeToFit style={{ color: 'black', textAlign: 'left', fontSize: 16, fontWeight: '600', marginVertical: 4 }}>{moment(dateSelected).format(globalDate)}</Text>
                        <Pressable onPress={() => { setDateSelected(null) }} onBlur={onBlur(schemaItem.title)}>
                          <Entypo name="calendar" size={24} color="black" style={{ textAlign: 'right', top: 5 }} />
                        </Pressable>
                      </View>
                      :
                      <Button customStyle={{ top: -5, alignSelf: "stretch", maxWidth: undefined, minWidth: "100%", borderRadius: 5 }} iconStyle={{ top: 5 }} defaultColor={buttonColor ? buttonColor : undefined} buttonText={"Select Date"} icon='calendar' onPress={() => setOpen(true)} />
                  }

                  {
                    dateSelected && timeSelected ?
                      <View style={{ flex: 1, minWidth: 100, flexDirection: 'row', justifyContent: 'space-between', backgroundColor: '#ececec', paddingHorizontal: 10, borderTopStartRadius: 0, borderBottomStartRadius: 0, marginVertical: 5, borderRadius: 20, padding: 5, gap: 10 }}>
                        <Text adjustsFontSizeToFit style={{ color: 'black', textAlign: 'left', fontSize: 16, fontWeight: '600', marginVertical: 4 }}>{moment(timeSelected).format('HH:MMa')}</Text>
                        <Pressable onPress={() => { setTimeSelected(null) }} onBlur={onBlur(schemaItem.title)}>
                          <AntDesign name="clock-circle" size={24} color="white" style={{ textAlign: 'right', top: 5 }} />
                        </Pressable>
                      </View>
                      :
                      <View style={{ flex: 0.6, left: -18, zIndex: -1 }}>
                        {dateSelected &&
                          <View  animation={'fadeIn'} style={{ flex: 1, top: 5 }}>
                            <Button customStyle={{ maxWidth: undefined, borderRadius: 20, minHeight: 48, minWidth: "100%", borderTopEndRadius: 0, borderBottomEndRadius: 0 }} iconOnly defaultColor={buttonColor ? buttonColor : undefined} icon='clock-circle' iconStyle={{ left: 5 }} iconColor='white' onPress={() => setTimeOpen(true)} />
                          </View>
                        }
                      </View>
                  }
                </View>
                :
                <View style={{ flex: 1 }}>
                  {
                    dateSelected ?
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 10, marginVertical: 5, borderRadius: 20, padding: 5 }}>
                        <Text adjustsFontSizeToFit style={{ textAlign: 'left', fontSize: 16, fontWeight: '600', marginVertical: 4 }}>{moment(dateSelected).format('dddd DD MM YYYY HH:MMa')}</Text>
                        <Pressable onPress={() => { setDateSelected(null) }} onBlur={onBlur(schemaItem.title)}>
                          <Entypo name="cross" size={24} color="black" style={{ textAlign: 'right' }} />
                        </Pressable>
                      </View>
                      :
                      <>
                        <DatePicker modal open={dateModal} onCancel={() => setDateModal(false)} onConfirm={(date) => { console.log(date), setDateSelected(date); handleDateSelection('', date); setDateModal(false) }} style={{ zIndex: 100 }} theme={theme ? "light" : "dark"} date={date} minuteInterval={15} onDateChange={(date) => { setDate(date); handleDateSelection(schemaItem?.content?.type, date) }} textColor={theme ? "black" : "white"} mode="datetime" />
                        <Button onPress={() => { setDateModal(true) }} customStyle={{ minHeight: 40, minWidth: 220, borderRadius: 5 }} buttonText={"Select Date and Time"} />
                      </>
                  }
                </View>
            }
          </View>
        </View>
      );
    case 'email':
      const [emailValue, setEmailValue] = useState(field.value?.toString() || '');
      const [isEmailFocused, setIsEmailFocused] = useState(false);

      // Email validation regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValidEmail = emailValue.length === 0 || emailRegex.test(emailValue);

      // Sync with field value changes
      React.useEffect(() => {
        if (field.value !== undefined && field.value?.toString() !== emailValue) {
          setEmailValue(field.value?.toString() || '');
        }
      }, [field.value]);

      const handleEmailChange = (text: string) => {
        setEmailValue(text);
        helpers.setValue(text);
        helpers.setTouched(true);

        // Real-time email validation
        if (text.length > 0 && !emailRegex.test(text)) {
          helpers.setError('Please enter a valid email address');
        } else {
          helpers.setError(null);
        }
      };

      const emailHasError = hasError || !isValidEmail;

      return (
        <View style={{ flex: 1, marginVertical: 10, ...props.customStyle }}>
          {/* Error display */}
          {emailHasError && (
            <Text

              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error || 'Please enter a valid email address'}
            </Text>
          )}

          {/* Email Input with Icon */}
          <View style={{ position: 'relative' }}>
            <TextInput
              ref={inputRef}
              value={emailValue}
              onChangeText={handleEmailChange}
              onFocus={() => {
                // Set focus time IMMEDIATELY and synchronously before any blur can occur
                if (Platform.OS === 'web') {
                  focusTimeRef.current = getCurrentTime();
                }
                setIsEmailFocused(true);
                if (props.focusCallback) props.focusCallback();
              }}
              onMouseDown={() => {
                // Set focus time even earlier - on mouse down/touch start
                if (Platform.OS === 'web' && isTouchDeviceRef.current) {
                  focusTimeRef.current = getCurrentTime();
                }
              }}
              onTouchStart={() => {
                // Set focus time even earlier - on touch start
                if (Platform.OS === 'web' && isTouchDeviceRef.current) {
                  focusTimeRef.current = getCurrentTime();
                }
              }}
              onBlur={(e) => {
                // On web touch devices, prevent blur if it happens too quickly after focus
                if (Platform.OS === 'web' && isTouchDeviceRef.current) {
                  const now = getCurrentTime();
                  const timeSinceFocus = now - focusTimeRef.current;
                  // Increased to 1000ms to be more aggressive in preventing false blurs
                  if (timeSinceFocus < 1000) {
                    // Start blur prevention to block validation and form remounting
                    blurPreventionState.startPrevention();
                    // Prevent default blur behavior
                    e?.preventDefault?.();
                    e?.stopPropagation?.();
                    // Refocus with multiple attempts to ensure it works
                    const refocus = () => {
                      if (inputRef.current) {
                        try {
                          inputRef.current.focus();
                        } catch (err) {
                          console.warn('Failed to refocus input:', err);
                        }
                      }
                    };
                    // Try immediately
                    refocus();
                    // Try after short delay
                    setTimeout(refocus, 50);
                    // Try after longer delay as fallback
                    setTimeout(refocus, 150);
                    return; // Don't process this blur event - CRITICAL: Don't call field.onBlur
                  }
                }
                // Clear blur prevention if it was active
                blurPreventionState.stopPrevention();
                setIsEmailFocused(false);
                field.onBlur(schemaItem.title);
                if (props.blurCallback) props.blurCallback();
              }}
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              placeholder={schemaItem.content?.label || "Email Address"}
              style={{
                backgroundColor: theme ? 'white' : '#1e293b',
                borderWidth: 1,
                borderColor: emailHasError ? '#ef4444' : (isEmailFocused ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
                borderRadius: 12,
                paddingHorizontal: 16,
                paddingVertical: 14,
                paddingLeft: 45, // Space for email icon
                fontSize: 16,
                color: theme ? '#1e293b' : '#f8fafc',
                minHeight: 50,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 3,
              }}
              {...(Platform.OS !== 'web' && {
                placeholderTextColor: theme ? '#64748b' : '#94a3b8',
                returnKeyType: index === length - 1 ? 'done' : 'next',
                onSubmitEditing: () => handleKeyPress(index),
              })}
            />

            {/* Email icon */}
            <View style={{
              position: 'absolute',
              left: 16,
              top: 0,
              bottom: 0,
              justifyContent: 'center',
              alignItems: 'center',
              width: 20,
              marginTop: 15
            }}>
              <Entypo
                name="email"
                size={18}
                color={isValidEmail ? (theme ? '#64748b' : '#94a3b8') : '#ef4444'}
              />
            </View>

            {/* Validation icon */}
            {emailValue.length > 0 && (
              <View style={{
                position: 'absolute',
                right: 16,
                top: 0,
                bottom: 0,
                justifyContent: 'center',
                alignItems: 'center',
                width: 20,
                marginTop: 15
              }}>
                <Entypo
                  name={isValidEmail ? 'check' : 'cross'}
                  size={16}
                  color={isValidEmail ? '#10b981' : '#ef4444'}
                />
              </View>
            )}
          </View>

          {/* Email validation feedback */}
          {emailValue.length > 0 && (
            <View style={{ marginTop: 6, flexDirection: 'row', alignItems: 'center' }}>
              <Entypo
                name={isValidEmail ? 'check' : 'warning'}
                size={12}
                color={isValidEmail ? '#10b981' : '#f59e0b'}
                style={{ marginRight: 6 }}
              />
              <Text style={{
                fontSize: 12,
                color: isValidEmail ? '#10b981' : '#f59e0b',
                fontWeight: '500'
              }}>
                {isValidEmail ? 'Valid email address' : 'Please enter a valid email format'}
              </Text>
            </View>
          )}

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 8,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}
        </View>
      );
    case 'password':
      const [passwordValue, setPasswordValue] = useState(field.value?.toString() || '');
      const [confirmPasswordValue, setConfirmPasswordValue] = useState('');
      const [isPasswordVisible, setIsPasswordVisible] = useState(false);
      const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
      const [isPasswordFocused, setIsPasswordFocused] = useState(false);
      const [isConfirmPasswordFocused, setIsConfirmPasswordFocused] = useState(false);

      const needsConfirmation = schemaItem.content?.validation?.repeatPassword;
      const minLength = schemaItem.content?.validation?.minLength || 6;

      // Password validation
      const passwordTooShort = passwordValue.length > 0 && passwordValue.length < minLength;
      const passwordsDoNotMatch = needsConfirmation && confirmPasswordValue.length > 0 && passwordValue !== confirmPasswordValue;

      // Sync with field value changes
      React.useEffect(() => {
        if (field.value !== undefined && field.value?.toString() !== passwordValue) {
          setPasswordValue(field.value?.toString() || '');
        }
      }, [field.value]);

      const handlePasswordChange = (text: string) => {
        setPasswordValue(text);
        helpers.setValue(text);
        helpers.setTouched(true);
      };

      const handleConfirmPasswordChange = (text: string) => {
        setConfirmPasswordValue(text);
        // Validate password match
        if (text !== passwordValue && text.length > 0) {
          helpers.setError('Passwords do not match');
        } else if (passwordValue.length >= minLength && text === passwordValue) {
          helpers.setError(null);
        }
      };

      return (
        <View style={{ flex: 1, marginVertical: 10, ...props.customStyle }}>
          {/* Error display */}
          {hasError && (
            <Text

              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error || schemaItem.content?.validation?.errorMessage}
            </Text>
          )}

          {/* Password Input */}
          <View style={{ position: 'relative' }}>
            <TextInput
              ref={inputRef}
              value={passwordValue}
              onChangeText={handlePasswordChange}
              onFocus={() => {
                setIsPasswordFocused(true);
                if (props.focusCallback) props.focusCallback();
              }}
              onBlur={() => {
                setIsPasswordFocused(false);
                field.onBlur(schemaItem.title);
                if (props.blurCallback) props.blurCallback();
              }}
              secureTextEntry={!isPasswordVisible}
              placeholder={schemaItem.content?.label || "Password"}
              style={{
                backgroundColor: theme ? 'white' : '#1e293b',
                borderWidth: 1,
                borderColor: hasError || passwordTooShort ? '#ef4444' : (isPasswordFocused ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
                borderRadius: 12,
                paddingHorizontal: 16,
                paddingVertical: 14,
                paddingRight: 50, // Space for eye icon
                fontSize: 16,
                color: theme ? '#1e293b' : '#f8fafc',
                minHeight: 50,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 3,
              }}
              {...(Platform.OS !== 'web' && {
                placeholderTextColor: theme ? '#64748b' : '#94a3b8',
                returnKeyType: needsConfirmation ? 'next' : (index === length - 1 ? 'done' : 'next'),
                onSubmitEditing: () => {
                  if (!needsConfirmation) {
                    handleKeyPress(index);
                  }
                },
              })}
            />

            {/* Password visibility toggle */}
            <Pressable
              onPress={() => setIsPasswordVisible(!isPasswordVisible)}
              style={{
                position: 'absolute',
                right: 16,
                top: 0,
                bottom: 0,
                justifyContent: 'center',
                alignItems: 'center',
                width: 24,
                height: 24,
                alignSelf: 'center',
                marginTop: 13
              }}
            >
              <Entypo
                name={isPasswordVisible ? 'eye-with-line' : 'eye'}
                size={20}
                color={theme ? '#64748b' : '#94a3b8'}
              />
            </Pressable>
          </View>

          {/* Password strength indicator */}
          {passwordValue.length > 0 && (
            <View style={{ marginTop: 8 }}>
              <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 4
              }}>
                <Text style={{
                  fontSize: 12,
                  color: passwordTooShort ? '#ef4444' : '#10b981',
                  fontWeight: '500'
                }}>
                  {passwordTooShort ? `Minimum ${minLength} characters required` : 'Password strength: Good'}
                </Text>
                <Text style={{
                  fontSize: 11,
                  color: theme ? '#64748b' : '#94a3b8'
                }}>
                  {passwordValue.length}/{minLength}
                </Text>
              </View>

              {/* Strength bar */}
              <View style={{
                height: 4,
                backgroundColor: theme ? '#e2e8f0' : '#374151',
                borderRadius: 2,
                overflow: 'hidden'
              }}>
                <View style={{
                  height: '100%',
                  width: `${Math.min((passwordValue.length / minLength) * 100, 100)}%`,
                  backgroundColor: passwordTooShort ? '#ef4444' : passwordValue.length >= minLength * 1.5 ? '#10b981' : '#f59e0b',
                  borderRadius: 2
                }} />
              </View>
            </View>
          )}

          {/* Confirm Password Input */}
          {needsConfirmation && (
            <View style={{ marginTop: 16, position: 'relative' }}>
              <TextInput
                value={confirmPasswordValue}
                onChangeText={handleConfirmPasswordChange}
                onFocus={() => setIsConfirmPasswordFocused(true)}
                onBlur={() => setIsConfirmPasswordFocused(false)}
                secureTextEntry={!isConfirmPasswordVisible}
                placeholder="Confirm Password"
                style={{
                  backgroundColor: theme ? 'white' : '#1e293b',
                  borderWidth: 1,
                  borderColor: passwordsDoNotMatch ? '#ef4444' : (isConfirmPasswordFocused ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
                  borderRadius: 12,
                  paddingHorizontal: 16,
                  paddingVertical: 14,
                  paddingRight: 50, // Space for eye icon
                  fontSize: 16,
                  color: theme ? '#1e293b' : '#f8fafc',
                  minHeight: 50,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.1,
                  shadowRadius: 4,
                  elevation: 3,
                }}
                {...(Platform.OS !== 'web' && {
                  placeholderTextColor: theme ? '#64748b' : '#94a3b8',
                  returnKeyType: index === length - 1 ? 'done' : 'next',
                  onSubmitEditing: () => handleKeyPress(index),
                })}
              />

              {/* Confirm password visibility toggle */}
              <Pressable
                onPress={() => setIsConfirmPasswordVisible(!isConfirmPasswordVisible)}
                style={{
                  position: 'absolute',
                  right: 16,
                  top: 0,
                  bottom: 0,
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: 24,
                  height: 24,
                  alignSelf: 'center',
                  marginTop: 13
                }}
              >
                <Entypo
                  name={isConfirmPasswordVisible ? 'eye-with-line' : 'eye'}
                  size={20}
                  color={theme ? '#64748b' : '#94a3b8'}
                />
              </Pressable>

              {/* Password match validation */}
              {confirmPasswordValue.length > 0 && (
                <View style={{ marginTop: 6, flexDirection: 'row', alignItems: 'center' }}>
                  <Entypo
                    name={passwordsDoNotMatch ? 'cross' : 'check'}
                    size={14}
                    color={passwordsDoNotMatch ? '#ef4444' : '#10b981'}
                    style={{ marginRight: 6 }}
                  />
                  <Text style={{
                    fontSize: 12,
                    color: passwordsDoNotMatch ? '#ef4444' : '#10b981',
                    fontWeight: '500'
                  }}>
                    {passwordsDoNotMatch ? 'Passwords do not match' : 'Passwords match'}
                  </Text>
                </View>
              )}
            </View>
          )}

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 8,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}

          {/* Password requirements */}
          <View style={{ marginTop: 8 }}>
            <Text style={{
              fontSize: 11,
              color: theme ? '#64748b' : '#94a3b8',
              marginBottom: 4
            }}>
              Password requirements:
            </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 2 }}>
              <Entypo
                name={passwordValue.length >= minLength ? 'check' : 'dot-single'}
                size={12}
                color={passwordValue.length >= minLength ? '#10b981' : theme ? '#64748b' : '#94a3b8'}
                style={{ marginRight: 4 }}
              />
              <Text style={{
                fontSize: 11,
                color: passwordValue.length >= minLength ? '#10b981' : theme ? '#64748b' : '#94a3b8'
              }}>
                At least {minLength} characters
              </Text>
            </View>
            {needsConfirmation && (
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Entypo
                  name={passwordValue && confirmPasswordValue && passwordValue === confirmPasswordValue ? 'check' : 'dot-single'}
                  size={12}
                  color={passwordValue && confirmPasswordValue && passwordValue === confirmPasswordValue ? '#10b981' : theme ? '#64748b' : '#94a3b8'}
                  style={{ marginRight: 4 }}
                />
                <Text style={{
                  fontSize: 11,
                  color: passwordValue && confirmPasswordValue && passwordValue === confirmPasswordValue ? '#10b981' : theme ? '#64748b' : '#94a3b8'
                }}>
                  Passwords must match
                </Text>
              </View>
            )}
          </View>
        </View>
      );
    case 'text':
    case 'string':
    default:
      const [textValue, setTextValue] = useState(field.value?.toString() || '');
      const [isTextFocused, setIsTextFocused] = useState(false);
      const isMultiline = schemaItem.content?.ref === 'multiline' || schemaItem.validation?.type === 'multiline';

      // Sync with field value changes
      React.useEffect(() => {
        if (field.value !== undefined && field.value?.toString() !== textValue) {
          setTextValue(field.value?.toString() || '');
        }
      }, [field.value]);

      const handleTextChange = (text: string) => {
        setTextValue(text);
        helpers.setValue(text);
        helpers.setTouched(true);
      };

      return (
        <View style={{ flex: 1, marginVertical: 10, ...props.customStyle }}>
          {/* Error display */}
          {hasError && (
            <Text

              style={{ color: 'red', padding: 2, fontSize: 12, marginBottom: 5 }}
            >
              {meta.error}
            </Text>
          )}

          {/* Modern Tamagui Text Input */}
          <TextInput
            ref={inputRef}
            value={textValue}
            onChangeText={handleTextChange}
            onFocus={() => {
              setIsTextFocused(true);
              if (props.focusCallback) props.focusCallback();
            }}
            onBlur={() => {
              setIsTextFocused(false);
              field.onBlur(schemaItem.title);
              if (props.blurCallback) props.blurCallback();
            }}
            multiline={isMultiline}
            numberOfLines={isMultiline ? 4 : 1}
            placeholder={schemaItem.validation?.placeHolder || schemaItem.content?.label || (schemaItem.title ? formatTitle(schemaItem.title) : "Enter text")}
            style={{
              backgroundColor: theme ? 'white' : '#1e293b',
              borderWidth: 1,
              borderColor: hasError ? '#ef4444' : (isTextFocused ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
              borderRadius: 12,
              paddingHorizontal: 16,
              paddingVertical: 14,
              fontSize: 16,
              color: theme ? '#1e293b' : '#f8fafc',
              minHeight: isMultiline ? 120 : 50,
              maxHeight: isMultiline ? 200 : 50,
              textAlignVertical: isMultiline ? 'top' : 'center',
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
            }}
            {...(Platform.OS !== 'web' && {
              placeholderTextColor: theme ? '#64748b' : '#94a3b8',
              returnKeyType: index === length - 1 ? 'done' : 'next',
              onSubmitEditing: () => {
                if (!isMultiline) {
                  handleKeyPress(index);
                }
              },
            })}
          />

          {/* Help text */}
          {schemaItem.content?.helpText && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 5,
              fontStyle: 'italic'
            }}>
              {schemaItem.content.helpText}
            </Text>
          )}

          {/* Character count for multiline */}
          {isMultiline && textValue && (
            <Text style={{
              fontSize: 11,
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 3,
              textAlign: 'right'
            }}>
              {textValue.length} characters
            </Text>
          )}

          {/* Max length validation */}
          {schemaItem.validation?.maxLength && (
            <Text style={{
              fontSize: 11,
              color: textValue.length > schemaItem.validation.maxLength ? '#ef4444' : theme ? '#64748b' : '#94a3b8',
              marginTop: 3,
              textAlign: 'right'
            }}>
              {textValue.length}/{schemaItem.validation.maxLength}
            </Text>
          )}
        </View>
      );
  }
}

const fieldStyles = StyleSheet.create({
  dropDownStyle: {
    borderColor: '#4791bd',
    borderRadius: 8,
    borderWidth: 1,
    minHeight: 50,
    paddingHorizontal: 15
  },
  containerStyle: {
    marginVertical: 5,
    zIndex: 1000
  },
  typeStyle: {
    minHeight: 40,
    fontWeight: 'bold',
    color: '#00000070',
    paddingHorizontal: 5
  }
})

export default memo(HandleFieldType);